import React, { useRef, useEffect, useCallback, useState } from "react";
import type { UseDrawingSelectionReturn } from "../hooks/useDrawingSelection";
import type { Token, MapNote, MapPin } from "../types";
import {
  clamp,
  worldToScreen,
  getViewportBounds,
  isInViewport,
  ZOOM_STEP,
} from "../utils";
import TokenActionBar from "./TokenActionBar";
import TokenHoverTooltip from "./TokenHoverTooltip";
import CharacterPortraits from "./CharacterPortraits";
import TimeOfDayHud from "./TimeOfDayHud";
import { CharacterHudOverlay } from "./character-hud/CharacterHudOverlay";
import { TIME_OF_DAY_OVERLAY } from "../lib/time-of-day-theme";
import { useCanvasContext } from "../contexts/CanvasContext";
import { useToolContext } from "../contexts/ToolContext";
import { useTokenContext } from "../contexts/TokenContext";
import { useSessionContext } from "../contexts/SessionContext";
import { useSelectionContext } from "../contexts/SelectionContext";
import { useSheetContext } from "../contexts/SheetContext";
import { useCanvasInteraction } from "../contexts/CanvasInteractionContext";
import { useI18n } from "../i18n";
import { renderAoeTemplates } from "../hooks/useAoeTemplates";
import { getWallStyle } from "../lib/wall-styles";
import { pointInPolygon } from "../utils/geometry";
import { normalizeConditionName, DND5E_CONDITION_LABELS_RU } from "@roll1/shared/systems/dnd5e/conditions.js";
import type { UseInpaintingReturn, MaskRegion } from "../hooks/useInpainting";
import type { UseDetailPassReturn } from "../hooks/useDetailPass";
import InpaintPromptPanel from "./InpaintPromptPanel";
import EdgeExpandButtons from "./EdgeExpandButtons";

const NOTE_ICON_MAP: Record<string, string> = {
  pin: "📌",
  scroll: "📜",
  skull: "💀",
  chest: "📦",
  star: "⭐",
  question: "❓",
  exclamation: "❗",
  camp: "🏕️",
  sword: "⚔️",
  key: "🔑",
};

/** Grid square size in feet — used for aura radius conversion */
const GRID_SIZE_FT = 5;

interface CanvasAreaProps {
  previewLabel?: string | null;
  getTokenDescription?: (token: Token) => string;
  linkDrawMode?: "rect" | "polygon" | "circle";
  mapObjectDrawMode?: "rect" | "polygon" | "circle";
  /** Ghost render callback from useAiPreview — registered after renderSelectionRect on UI layer */
  aiPreviewRender?: (() => void) | null;
  /** Inpainting state and actions — passed from AppShell */
  inpainting?: UseInpaintingReturn | null;
  /** Detail pass state and actions — passed from AppShell */
  detailPass?: UseDetailPassReturn | null;
  /** Outpainting state and actions — passed from AppShell */
  outpainting?: import("../hooks/useOutpainting").UseOutpaintingReturn | null;
  /** Callback to open outpaint dialog with a direction */
  onOpenOutpaint?: (direction: import("../hooks/useOutpainting").OutpaintDirection) => void;
  onContextMenuLink?: (
    link: import("@roll1/shared").LocationLink,
    x: number,
    y: number,
  ) => void;
  onOpenSheet?: (id: string) => void;
  /** Direct sheet ID for opening HUD without token selection */
  directSheetId?: string | null;
  /** Callback to clear directSheetId */
  onClearDirectSheetId?: () => void;
  avatarSize?: import("./CharacterPortraits").AvatarSize;
  onAvatarSizeChange?: (
    size: import("./CharacterPortraits").AvatarSize,
  ) => void;
  drawingSelection?: UseDrawingSelectionReturn;
  aoeTemplates?: Map<string, import("@roll1/shared").AoeTemplate>;
  aoeAffectedTokenIds?: Map<string, string>;
  aoePlacement?: {
    state: "idle" | "placing" | "rotating";
    previewTemplate: Omit<
      import("@roll1/shared").AoeTemplate,
      "id" | "placedBy"
    > | null;
    handleMouseMove: (
      canvasX: number,
      canvasY: number,
      shiftKey: boolean,
      gridSize: number,
    ) => void;
    handleClick: (
      canvasX: number,
      canvasY: number,
      shiftKey: boolean,
      gridSize: number,
    ) => void;
    handleRightClick: () => void;
    handleKeyDown: (key: string) => void;
    handleWheel: (deltaY: number) => void;
  };
}

const CanvasArea = React.memo(function CanvasArea({
  previewLabel,
  getTokenDescription,
  linkDrawMode = "rect",
  mapObjectDrawMode = "rect",
  onContextMenuLink,
  onOpenSheet,
  directSheetId,
  onClearDirectSheetId,
  avatarSize,
  onAvatarSizeChange,
  drawingSelection,
  aoeTemplates,
  aoeAffectedTokenIds,
  aoePlacement,
  aiPreviewRender,
  inpainting,
  detailPass,
  outpainting,
  onOpenOutpaint,
}: CanvasAreaProps) {
  const canvasCtx = useCanvasContext();
  const toolCtx = useToolContext();
  const tokenCtx = useTokenContext();
  const sessionCtx = useSessionContext();
  const selectionCtx = useSelectionContext();
  const { sheets: charSheets } = useSheetContext();
  const { get, register } = useCanvasInteraction();
  const pageId = sessionCtx.outgoingPageId;
  const { t } = useI18n();

  const engine = canvasCtx.engine;
  const tokens = tokenCtx.tokens;
  const drawing = canvasCtx.drawing;
  const fog = canvasCtx.fog;
  const dynamicFog = canvasCtx.dynamicFog;
  const currentTool = toolCtx.state.active;
  const snapToGrid = canvasCtx.snapToGrid;
  const locLinks = canvasCtx.locationLinks;
  const mapObjectsLocal = canvasCtx.mapObjects;
  const notes = tokenCtx.notes.notes;
  const pins = tokenCtx.pins.pins;
  const isGM = sessionCtx.isGM;
  const hpBarVisibility = sessionCtx.sessionSettings?.hpBarVisibility ?? 'hover';
  const hpBarVisibilityRef = useRef(hpBarVisibility);
  useEffect(() => { hpBarVisibilityRef.current = hpBarVisibility; }, [hpBarVisibility]);

  // Token hit-test filter: skip tokens invisible to the player (hidden, or in dark mode outside lit zones)
  const tokenHitFilter = useCallback((t: Token): boolean => {
    if (isGM) return true;
    if (!t.visible) return false;
    // In dynamic fog, skip tokens outside player's effective visibility
    if (fog.fogModeRef.current === 'dynamic' && fog.enabled.current) {
      const controlledIds = canvasCtx.controlledTokenIdsRef.current;
      if (controlledIds.has(t.id)) return true; // always can click own token
      const visPoly = dynamicFog.visibilityPolygonsRef.current;
      // No visibility polygons → no non-controlled tokens are clickable
      if (!visPoly || visPoly.length === 0) return false;
      let inVis = false;
      for (const poly of visPoly) {
        if (pointInPolygon(t.x, t.y, { points: poly })) { inVis = true; break; }
      }
      if (!inVis) return false;
      // Dark mode: also check lit zones
      const gi = canvasCtx.globalIlluminationRef.current;
      const litZones = dynamicFog.litZoneCacheRef.current;
      if (gi.level === 'dark' && litZones) {
        for (const zone of litZones) {
          if (zone.dimPolygon.length >= 3 && pointInPolygon(t.x, t.y, { points: zone.dimPolygon })) return true;
        }
        return false;
      }
    }
    return true;
  }, [isGM, fog, dynamicFog, canvasCtx]);

  const aoeAffectedRef = useRef<Map<string, string>>(new Map());
  aoeAffectedRef.current = aoeAffectedTokenIds ?? new Map();

  // Derive tokenActions from selected token's character sheet (D-306).
  // Gated by the server's roll:request authorization rule (roll.ts: GM or
  // sheet owner) — without the gate any player who selected the token saw an
  // action bar whose every click the server rejects with 'unauthorized'.
  const resolvedTokenActions = (() => {
    const sel = tokens.selectedToken;
    if (!sel?.characterSheetId) return undefined;
    const sheet = charSheets.sheets.find((s) => s.id === sel.characterSheetId);
    if (!sheet) return undefined;
    const canRoll = isGM || sheet.ownerName === sessionCtx.mp.participant?.name;
    return canRoll ? sheet.tokenActions : undefined;
  })();
  const resolvedOnTokenAction = get("token:action");

  // ── Load token images (own imageSrc or sheet portrait) ──
  const imageLoadedIds = useRef<Set<string>>(new Set());
  // Per-token failed-load counter. A token is pinned in imageLoadedIds while a
  // load is in-flight (dedupes the render loop's per-frame calls); on failure
  // we un-pin so a transient error (network blip / evicted-cache miss) retries,
  // but cap attempts so a genuine 404 doesn't hammer the network every frame.
  const imageLoadFails = useRef<Map<string, number>>(new Map());
  const sheetsRef = useRef(charSheets.sheets);
  sheetsRef.current = charSheets.sheets;

  const loadTokenImage = useCallback((tokenId: string, src: string) => {
    if (imageLoadedIds.current.has(tokenId)) return;
    imageLoadedIds.current.add(tokenId); // mark in-flight (dedupe render-loop calls)

    const MAX_RETRIES = 3;
    // On a failed load, un-pin so the next render-loop pass retries — but only
    // up to MAX_RETRIES, after which we leave it pinned (give up, show the
    // fallback circle) so a permanent 404 doesn't reload every frame. Without
    // this, a single transient failure left the token blank forever (until F5).
    const onFail = () => {
      const n = (imageLoadFails.current.get(tokenId) ?? 0) + 1;
      imageLoadFails.current.set(tokenId, n);
      if (n < MAX_RETRIES) imageLoadedIds.current.delete(tokenId);
    };

    if (isAnimatedSrc(src)) {
      // Animated: decode frames via ImageDecoder API
      if (animatedLoading.current.has(tokenId)) return;
      animatedLoading.current.add(tokenId);
      import('../lib/animated-image').then(({ loadAnimatedImage }) => {
        loadAnimatedImage(src).then((anim) => {
          animatedImages.current.set(tokenId, anim);
          // Still need a static imageEl for cache key / fallback
          const img = new Image();
          img.onload = () => {
            tokens.updateToken(tokenId, { imageSrc: src, imageEl: img });
            engine.markDirty('token');
          };
          img.onerror = () => { animatedLoading.current.delete(tokenId); onFail(); };
          img.src = src;
        }).catch(() => { animatedLoading.current.delete(tokenId); onFail(); });
      });
      return;
    }

    // Static image
    const img = new Image();
    img.onload = () => {
      tokens.updateToken(tokenId, { imageSrc: src, imageEl: img });
      engine.markDirty('token');
    };
    img.onerror = onFail;
    img.src = src;
  }, [tokens, engine]);

  // Batch D (live portrait): a token with no own image displays its sheet's
  // portrait (fallback in the render loop, which then sets imageSrc = portrait
  // url). imageLoadedIds pins it, so a later sheet:portrait-updated changed
  // only sheet.portrait and the token kept the STALE portrait until reload.
  // Watch sheet portraits; when one changes, invalidate the current-page
  // portrait-backed tokens (imageSrc null or still equal to the OLD portrait —
  // tokens with their own uploaded image are untouched) so the render loop
  // re-loads the new portrait.
  //
  // Scope: CURRENT page only. Tokens on other pages keep their locally-cached
  // imageSrc (page store) and update on next page-load or full reload — same
  // as the pre-D2 behavior, not a regression, just not covered live.
  const prevPortraits = useRef<Map<string, string | null>>(new Map());
  useEffect(() => {
    const liveSheetIds = new Set<string>();
    let invalidated = false;
    for (const sheet of charSheets.sheets) {
      liveSheetIds.add(sheet.id);
      const curr = sheet.portrait ?? null;
      const prev = prevPortraits.current.get(sheet.id);
      prevPortraits.current.set(sheet.id, curr);
      if (prev === undefined || prev === curr) continue; // first-seen or unchanged
      for (const tk of tokens.tokens) {
        if (tk.characterSheetId !== sheet.id) continue;
        if (tk.imageSrc && tk.imageSrc !== prev) continue; // token has its own image
        imageLoadedIds.current.delete(tk.id);
        imageLoadFails.current.delete(tk.id); // new portrait → fresh retry budget
        tokens.updateToken(tk.id, { imageEl: null, imageSrc: null });
        invalidated = true;
      }
    }
    // Prune entries for deleted sheets so the map can't grow unbounded.
    for (const id of prevPortraits.current.keys()) {
      if (!liveSheetIds.has(id)) prevPortraits.current.delete(id);
    }
    if (invalidated) engine.markDirty('token');
  }, [charSheets.sheets, tokens, engine]);

  // ── Stable closure ref (same pattern as useAppShortcuts) ──
  const d = useRef({ engine, tokens, drawing, fog, currentTool, snapToGrid, locLinks, mapObjects: mapObjectsLocal, notes, pins, isGM, selectionCtx, sessionCtx, pageId, drawingSelection, get, onContextMenuLink, getTokenDescription, onOpenSheet, linkDrawMode, mapObjectDrawMode, aoeTemplates, aoePlacement });
  useEffect(() => { d.current = { engine, tokens, drawing, fog, currentTool, snapToGrid, locLinks, mapObjects: mapObjectsLocal, notes, pins, isGM, selectionCtx, sessionCtx, pageId, drawingSelection, get, onContextMenuLink, getTokenDescription, onOpenSheet, linkDrawMode, mapObjectDrawMode, aoeTemplates, aoePlacement }; });

  const isMouseDown = useRef(false);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const spaceDown = useRef(false);
  const dragTokens = useRef<{
    items: {
      token: Token;
      offsetX: number;
      offsetY: number;
      origX: number;
      origY: number;
    }[];
  } | null>(null);
  /** Original position of the first dragged token (for drag ruler start). */
  const dragTokenOrigin = useRef<{ x: number; y: number } | null>(null);
  const dragNote = useRef<{
    note: MapNote;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const dragPin = useRef<{
    pinId: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const fogRevealStartIdx = useRef(0);
  const fogCloseStartIdx = useRef(0);
  const [hoveredNote, setHoveredNote] = useState<{
    note: MapNote;
    sx: number;
    sy: number;
  } | null>(null);
  // Link hover tooltip
  const [hoveredLinkTooltip, setHoveredLinkTooltip] = useState<{
    label: string;
    sx: number;
    sy: number;
  } | null>(null);
  // Link drawing state
  const linkDrawStart = useRef<{ x: number; y: number } | null>(null);
  const linkPolygonPoints = useRef<{ x: number; y: number }[]>([]);
  const lastPolygonClickTime = useRef(0);

  // Map-object drag state (for moving selected MapObjects)
  const dragMapObject = useRef<{
    objectId: string;
    origRegion: import("@roll1/shared").LinkRegion;
    startX: number;
    startY: number;
  } | null>(null);

  // Map-object drawing state (mirrors link drawing)
  const mapObjDrawStart = useRef<{ x: number; y: number } | null>(null);
  const mapObjPolygonPoints = useRef<{ x: number; y: number }[]>([]);
  const lastMapObjPolygonClickTime = useRef(0);

  // Ping state
  interface PingMarker { x: number; y: number; startTime: number; by: string; color?: string }
  const pingsRef = useRef<PingMarker[]>([]);
  const pingAnimRef = useRef<number>(0);
  const pingLongPressTimer = useRef<number>(0);
  const pingLongPressPos = useRef<{ x: number; y: number } | null>(null);

  const addPing = useCallback((x: number, y: number, by: string, color?: string) => {
    const { engine } = d.current;
    pingsRef.current.push({ x, y, startTime: Date.now(), by, color });
    engine.markDirty('ui');
    // Start animation loop if not already running
    if (!pingAnimRef.current) {
      const animate = () => {
        const now = Date.now();
        pingsRef.current = pingsRef.current.filter(p => now - p.startTime < 3000);
        d.current.engine.markDirty('ui');
        if (pingsRef.current.length > 0) {
          pingAnimRef.current = requestAnimationFrame(animate);
        } else {
          pingAnimRef.current = 0;
        }
      };
      pingAnimRef.current = requestAnimationFrame(animate);
    }
  }, []);

  // Register ping:add for WS sync
  useEffect(() => register("ping:add", addPing), [register, addPing]);

  // Register inpaint:region-drawn callback — connects canvas selection to useInpainting hook
  useEffect(() => {
    if (!inpainting) return;
    return register("inpaint:region-drawn", (region: MaskRegion) => {
      inpainting.setRegion(region);
    });
  }, [register, inpainting]);

  // Register detail:region-drawn callback — connects canvas selection to useDetailPass hook
  useEffect(() => {
    if (!detailPass) return;
    return register("detail:region-drawn", (region: { x: number; y: number; width: number; height: number }) => {
      detailPass.setRegion(region);
    });
  }, [register, detailPass]);

  // Reset inpaint state when tool changes away from inpaint tools
  useEffect(() => {
    if (currentTool !== "inpaint-rect" && currentTool !== "inpaint-lasso") {
      inpaintRectStart.current = null;
      inpaintRectEnd.current = null;
      inpaintLassoPoints.current = [];
    }
    if (currentTool !== "detail-rect") {
      detailRectStart.current = null;
      detailRectEnd.current = null;
    }
  }, [currentTool]);

  // Reset link/map-object drawing state when tool changes or draw mode switches
  useEffect(() => {
    linkDrawStart.current = null;
    linkPolygonPoints.current = [];
    mapObjDrawStart.current = null;
    mapObjPolygonPoints.current = [];
    // Clear drawing selection when switching away from select-drawing
    if (currentTool !== "select-drawing" && drawingSelection) {
      drawingSelection.clearSelection();
    }
  }, [currentTool, linkDrawMode, mapObjectDrawMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Whether a measurement is currently active (stays after first click for waypoint mode). */
  const measureActive = useRef(false);
  /** Screen position where the current measure mousedown started (for drag detection). */
  const measureDownScreenPos = useRef<{ x: number; y: number } | null>(null);
  /** Pixels of movement before a mousedown is considered a drag (not a click). */
  const MEASURE_DRAG_THRESHOLD = 6;
  /** Last known snapped world cursor position (for Q-key waypoint insertion). */
  const lastWorldPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  /** Whether the token drag ruler has been started for the current drag. */
  const tokenDragRulerStarted = useRef(false);

  // Token hover tooltip
  const [hoverToken, setHoverToken] = useState<{
    token: Token;
    description: string;
    screenX: number;
    screenY: number;
  } | null>(null);
  const hoverTokenIdRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Token click-to-inspect: suppress click when double-click detected
  const clickedTokenRef = useRef<Token | null>(null);
  const didDragRef = useRef(false);
  const doubleClickSuppressRef = useRef(false);

  // Marquee (rubber-band) selection rectangle in world coordinates
  const selectionRect = useRef<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);

  // Inpaint selection state (in map-space coordinates)
  const inpaintRectStart = useRef<{ x: number; y: number } | null>(null);
  const inpaintRectEnd = useRef<{ x: number; y: number } | null>(null);
  const inpaintLassoPoints = useRef<Array<{ x: number; y: number }>>([]);
  const inpaintPreviewImage = useRef<HTMLImageElement | null>(null);

  // Detail-rect selection state (in map-space coordinates)
  const detailRectStart = useRef<{ x: number; y: number } | null>(null);
  const detailRectEnd = useRef<{ x: number; y: number } | null>(null);

  // Anchor point for the inpaint prompt panel (screen coords)
  const inpaintPanelAnchor = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Helper: find note at world position
  const getNoteAtWorld = useCallback(
    (wx: number, wy: number): MapNote | null => {
      const { engine, notes } = d.current;
      const cam = engine.camera;
      const PIN_HIT_RADIUS = Math.max(20, 14 / cam.zoom); // fixed screen-space hit area
      for (let i = notes.length - 1; i >= 0; i--) {
        const n = notes[i];
        const dx = n.x - wx;
        const dy = n.y - wy;
        if (dx * dx + dy * dy <= PIN_HIT_RADIUS * PIN_HIT_RADIUS) return n;
      }
      return null;
    },
    [],
  );

  // Helper: find pin at world position (for SelectionContext wiring)
  const getPinAtWorld = useCallback(
    (wx: number, wy: number): MapPin | null => {
      const { engine, pins, isGM } = d.current;
      const cam = engine.camera;
      const PIN_HIT_RADIUS = Math.max(20, 14 / cam.zoom);
      for (let i = pins.length - 1; i >= 0; i--) {
        const p = pins[i];
        // GM-only pins are not clickable for players
        if (!isGM && p.visibility === 'gm') continue;
        const dx = p.x - wx;
        const dy = p.y - wy;
        if (dx * dx + dy * dy <= PIN_HIT_RADIUS * PIN_HIT_RADIUS) return p;
      }
      return null;
    },
    [],
  );

  // ── Token pre-render cache (Phase 3, Fix 9) ──
  interface TokenCacheEntry {
    canvas: HTMLCanvasElement;
    key: string;
    /** Logical (CSS-pixel) width of the offscreen canvas */
    logicalW: number;
    /** Logical (CSS-pixel) height of the offscreen canvas */
    logicalH: number;
    /** Horizontal offset from cache left edge to token left edge (CSS pixels) */
    offsetX: number;
  }
  const tokenCacheMap = useRef<Map<string, TokenCacheEntry>>(new Map());

  // Invalidate all caches when DPR changes (e.g., monitor switch, OS scale change)
  useEffect(() => {
    if (engine.dprVersion === 0) return; // skip initial
    tokenCacheMap.current.clear();
    drawing.invalidateCache();
    fog.invalidateCache();
    engine.markAllDirty();
  }, [engine.dprVersion, engine, drawing, fog]);

  // V004: Invalidate token caches when hpBarVisibility changes
  useEffect(() => {
    tokenCacheMap.current.clear();
    engine.markDirty('token');
  }, [hpBarVisibility, engine]);

  // Dispose all animated images on unmount
  useEffect(() => {
    const anims = animatedImages.current;
    return () => {
      for (const anim of anims.values()) anim.dispose();
      anims.clear();
    };
  }, []);

  /** Shared temp canvas for text measurement (avoids DOM allocation per token per frame) */
  const measureCanvas = useRef<HTMLCanvasElement | null>(null);
  function getMeasureCtx(): CanvasRenderingContext2D {
    if (!measureCanvas.current)
      measureCanvas.current = document.createElement("canvas");
    return measureCanvas.current.getContext("2d")!;
  }

  function tokenCacheKey(t: Token, dpr: number): string {
    return `${t.id}|${t.imageSrc ?? ""}|${t.size}|${t.color}|${t.hp}|${t.maxHp}|${t.conditions.join(",")}|${t.name}|${Math.round(dpr * 100)}|${hpBarVisibilityRef.current}`;
  }

  /** Reference zoom level for token pre-render cache — tokens are rendered at this zoom and scaled via drawImage */
  const TOKEN_CACHE_ZOOM = 2;

  /** Detect animated image formats (GIF, APNG, animated WebP) */
  function isAnimatedSrc(src: string | null): boolean {
    if (!src) return false;
    const lower = src.toLowerCase();
    return lower.endsWith('.gif') || lower.endsWith('.apng') ||
      lower.includes('.gif?') || lower.includes('.apng?');
  }

  /** RAF handle for continuous token animation */
  const tokenAnimRAF = useRef<number>(0);

  /** Map tokenId → AnimatedImage for frame-by-frame GIF/APNG rendering */
  const animatedImages = useRef<Map<string, import('../lib/animated-image').AnimatedImage>>(new Map());
  const animatedLoading = useRef<Set<string>>(new Set());

  // Render tokens (auras first, then tokens on top)
  // Reads from refs (tokensRef, selectedTokenIdsRef) so the callback is stable across state changes.
  const renderTokens = useCallback(() => {
    const canvas = engine.refs.current.token;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cam = engine.cameraRef.current;
    const gs = engine.gridSize;
    const cssW = canvas.width / dpr;
    const cssH = canvas.height / dpr;
    ctx.clearRect(0, 0, cssW, cssH);

    const allTokens = tokens.tokensRef.current;
    if (!allTokens) return;
    const selIds = tokens.selectedTokenIdsRef.current;

    // ── Viewport culling bounds (world coords) ──
    const vp = getViewportBounds(cam, cssW, cssH);

    // Dynamic fog visibility filter: hide tokens outside current visibility for players
    const isDynamicFog = !isGM && fog.fogModeRef.current === 'dynamic' && fog.enabled.current;
    const visPoly = isDynamicFog ? dynamicFog.visibilityPolygonsRef.current : null;
    const controlledIds = canvasCtx.controlledTokenIdsRef.current;
    const giLevel = canvasCtx.globalIlluminationRef.current.level;
    const litZones = isDynamicFog ? dynamicFog.litZoneCacheRef.current : null;

    // Helper: check if point is within any polygon set
    const isPointInAnyPolygon = (px: number, py: number, polys: Array<{ points: Array<{ x: number; y: number }> }>): boolean => {
      for (const poly of polys) {
        if (pointInPolygon(px, py, poly)) return true;
      }
      return false;
    };

    // Pre-compute wrapped polygons for reuse
    const wrappedVis = visPoly ? visPoly.map(p => ({ points: p })) : null;
    // In dark mode, build lit zone polygons for additional visibility check
    const wrappedLit: Array<{ points: Array<{ x: number; y: number }> }> | null =
      (giLevel === 'dark' && litZones)
        ? litZones.filter(z => z.dimPolygon.length >= 3).map(z => ({ points: z.dimPolygon }))
        : null;

    // Helper: check if a token is effectively visible to the player.
    // In dark mode, OTHER tokens must also be within a lit zone.
    // Player's own controlled tokens are always visible (isControlled = true).
    const isTokenInVisibility = (tx: number, ty: number, size: number, isControlled: boolean): boolean => {
      // Controlled tokens (player's own) are always visible to that player
      if (isControlled) return true;
      // No visibility polygons: in dynamic fog, hide non-controlled tokens;
      // outside dynamic fog, show everything
      if (!wrappedVis || wrappedVis.length === 0) return !isDynamicFog;

      const checkPoint = (px: number, py: number): boolean => {
        if (!isPointInAnyPolygon(px, py, wrappedVis)) return false;
        // In dark mode: other tokens must also be within a lit zone
        if (wrappedLit && wrappedLit.length > 0 && !isPointInAnyPolygon(px, py, wrappedLit)) return false;
        return true;
      };

      if (checkPoint(tx, ty)) return true;
      if (size > 1) {
        const r = (size * gs) / 2;
        const offsets = [
          [r, 0], [-r, 0], [0, r], [0, -r],
          [r, r], [r, -r], [-r, r], [-r, -r],
        ];
        for (const [dx, dy] of offsets) {
          if (checkPoint(tx + dx, ty + dy)) return true;
        }
      }
      return false;
    };

    // --- Render auras BELOW tokens ---
    const now = Date.now();
    for (const t of allTokens) {
      if (!t || t.x == null) continue;
      if (!t.visible && !isGM) continue;
      if (!t.auras || t.auras.length === 0) continue;
      // Hide auras of tokens outside visibility for players
      if (!isTokenInVisibility(t.x, t.y, t.size, controlledIds.has(t.id))) continue;

      // Viewport culling for auras — compute max aura radius (includes token size offset)
      const maxAuraRadius = Math.max(
        ...t.auras.map((a) => (a.radiusFt / GRID_SIZE_FT + t.size / 2) * gs),
        0,
      );
      if (!isInViewport(t.x, t.y, maxAuraRadius + (t.size * gs) / 2, vp))
        continue;

      const centerX = t.x * cam.zoom + cam.offsetX;
      const centerY = t.y * cam.zoom + cam.offsetY;
      // Hidden tokens shown at 40% opacity for GM so they can see radius coverage
      const ghostFactor = t.visible ? 1 : 0.4;

      for (const aura of t.auras) {
        const tokenRadiusCells = t.size / 2;
        const radiusPx =
          (aura.radiusFt / GRID_SIZE_FT + tokenRadiusCells) * gs * cam.zoom;
        if (radiusPx <= 0) continue;

        ctx.save();
        let opacity = aura.opacity;
        if (aura.style === "pulse") {
          // Oscillate opacity between 50% and 150% of base
          opacity *= 0.5 + 0.5 * Math.sin(now / 600);
          opacity = Math.max(0, Math.min(1, opacity));
        }

        // Semi-transparent fill
        ctx.globalAlpha = opacity * ghostFactor;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radiusPx, 0, Math.PI * 2);
        ctx.fillStyle = aura.color;
        ctx.fill();

        // Slightly brighter border
        ctx.globalAlpha = Math.min(1, opacity + 0.3) * ghostFactor;
        ctx.lineWidth = 2;
        if (aura.style === "dashed") {
          ctx.setLineDash([8, 4]);
        }
        ctx.strokeStyle = aura.color;
        ctx.stroke();
        ctx.setLineDash([]);

        // Label
        if (aura.label) {
          ctx.globalAlpha = Math.min(1, opacity + 0.5) * ghostFactor;
          ctx.fillStyle = "#fff";
          ctx.font = `${Math.max(9, Math.round(10 * cam.zoom))}px sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(aura.label, centerX, centerY - radiusPx - 3);
        }

        ctx.restore();
      }
    }

    // --- Render tokens ---
    // Clean up stale token cache entries (tokens removed from map)
    const activeIds = new Set(allTokens.map((t) => t.id));
    for (const [id] of tokenCacheMap.current) {
      if (!activeIds.has(id)) tokenCacheMap.current.delete(id);
    }
    // Clean up stale animated image entries
    for (const [id, anim] of animatedImages.current) {
      if (!activeIds.has(id)) {
        anim.dispose();
        animatedImages.current.delete(id);
        animatedLoading.current.delete(id);
        imageLoadedIds.current.delete(id);
      }
    }
    // Prune failed-load counters for removed tokens (bounded growth — covers
    // static-image tokens too, which the animated-image loop above misses).
    for (const [id] of imageLoadFails.current) {
      if (!activeIds.has(id)) imageLoadFails.current.delete(id);
    }

    let hasAnimated = false;

    for (const t of allTokens) {
      if (!t || t.x == null) continue;
      if (!t.visible && !isGM) continue;

      // In dynamic fog mode, player only sees tokens within visibility polygons
      if (!isTokenInVisibility(t.x, t.y, t.size, controlledIds.has(t.id))) continue;

      // Viewport culling for tokens
      const tokenRadius = (t.size * gs) / 2;
      if (!isInViewport(t.x, t.y, tokenRadius + 30, vp)) continue;

      const size = t.size * gs * cam.zoom;
      const sx = t.x * cam.zoom + cam.offsetX - size / 2;
      const sy = t.y * cam.zoom + cam.offsetY - size / 2;

      // ── Load image if missing (own imageSrc or sheet portrait) ──
      if (!t.imageEl && !imageLoadedIds.current.has(t.id)) {
        let imgSrc = t.imageSrc;
        if (!imgSrc && t.characterSheetId) {
          const sheet = sheetsRef.current.find(s => s.id === t.characterSheetId);
          if (sheet?.portrait) imgSrc = sheet.portrait;
        }
        if (imgSrc) loadTokenImage(t.id, imgSrc);
      }

      // ── Animated tokens: draw current frame directly (no cache) ──
      const anim = animatedImages.current.get(t.id);
      if (anim && anim.frameCount > 1) {
        hasAnimated = true;
        const frame = anim.getFrame();
        if (!frame) continue;
        const isHidden = !t.visible;
        if (isHidden) { ctx.save(); ctx.globalAlpha = 0.4; }
        const r = Math.max(0, size / 2 - 2);
        ctx.save();
        ctx.beginPath();
        ctx.arc(sx + size / 2, sy + size / 2, r, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(frame, sx, sy, size, size);
        ctx.restore();
        // Border
        ctx.beginPath();
        ctx.arc(sx + size / 2, sy + size / 2, r, 0, Math.PI * 2);
        ctx.strokeStyle = t.color;
        ctx.lineWidth = 3;
        ctx.stroke();
        // Name
        ctx.fillStyle = "#fff";
        ctx.font = `${Math.max(10, Math.round(11 * cam.zoom))}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(t.name, sx + size / 2, sy + size + 4);
        // HP bar (V004: respect hpBarVisibility setting; 'hover' shows the
        // bar for the currently hovered token — previously unhandled, so the
        // DEFAULT setting rendered no HP bar at all)
        const hpVis = hpBarVisibilityRef.current;
        const showHp = t.maxHp > 0 && (
          hpVis === 'always' ||
          (hpVis === 'gm-only' && isGM) ||
          (hpVis === 'hover' && hoverTokenIdRef.current === t.id)
        );
        if (showHp) {
          const barW = size * 0.8;
          const barH = 4 * cam.zoom;
          const barX = sx + (size - barW) / 2;
          const barY = sy + size + 1;
          const ratio = clamp(t.hp / t.maxHp, 0, 1);
          ctx.fillStyle = "rgba(0,0,0,0.5)";
          ctx.fillRect(barX, barY, barW, barH);
          ctx.fillStyle = ratio > 0.5 ? "#2ecc71" : ratio > 0.25 ? "#f39c12" : "#e74c3c";
          ctx.fillRect(barX, barY, barW * ratio, barH);
        }
        // Selection
        if (selIds.has(t.id)) {
          ctx.strokeStyle = "#fff";
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 3]);
          ctx.strokeRect(sx - 4, sy - 4, size + 8, size + 8);
          ctx.setLineDash([]);
        }
        if (isHidden) ctx.restore();
        continue;
      }

      // ── Token pre-render cache (zoom-independent, DPR-scaled for HiDPI) ──
      const padding = 20;
      const cacheKey = tokenCacheKey(t, dpr);
      let cached = tokenCacheMap.current.get(t.id);
      if (!cached || cached.key !== cacheKey) {
        // (Re)render token to offscreen canvas at reference zoom (TOKEN_CACHE_ZOOM)
        const refSize = t.size * gs * TOKEN_CACHE_ZOOM;

        // Measure name width to prevent clipping
        const nameFont = `${Math.max(10, 11)}px sans-serif`;
        const mCtx = getMeasureCtx();
        mCtx.font = nameFont;
        const nameWidth = mCtx.measureText(t.name).width;
        const contentW = Math.max(refSize, nameWidth + 4);

        const totalW = Math.ceil(contentW + padding);
        const totalH = Math.ceil(refSize + padding + 30);
        const offCanvas = cached?.canvas ?? document.createElement("canvas");
        offCanvas.width = Math.round(totalW * dpr);
        offCanvas.height = Math.round(totalH * dpr);
        const oc = offCanvas.getContext("2d")!;
        oc.setTransform(dpr, 0, 0, dpr, 0, 0);
        oc.clearRect(0, 0, totalW, totalH);

        // Center token horizontally within the (possibly wider) canvas
        const ox = (totalW - refSize) / 2;
        const oy = padding / 2;
        const tokenRadius = Math.max(0, refSize / 2 - 2);

        // Draw token
        oc.save();
        if (t.imageEl) {
          oc.beginPath();
          oc.arc(
            ox + refSize / 2,
            oy + refSize / 2,
            tokenRadius,
            0,
            Math.PI * 2,
          );
          oc.clip();
          oc.drawImage(t.imageEl, ox, oy, refSize, refSize);
          oc.restore();
          oc.save();
          oc.beginPath();
          oc.arc(
            ox + refSize / 2,
            oy + refSize / 2,
            tokenRadius,
            0,
            Math.PI * 2,
          );
          oc.strokeStyle = t.color;
          oc.lineWidth = 3;
          oc.stroke();
        } else {
          oc.beginPath();
          oc.arc(
            ox + refSize / 2,
            oy + refSize / 2,
            tokenRadius,
            0,
            Math.PI * 2,
          );
          oc.fillStyle = t.color;
          oc.globalAlpha = 0.85;
          oc.fill();
          oc.globalAlpha = 1;
          oc.strokeStyle = "rgba(255,255,255,0.6)";
          oc.lineWidth = 2;
          oc.stroke();
          oc.fillStyle = "#fff";
          oc.font = `bold ${Math.round(refSize * 0.4)}px sans-serif`;
          oc.textAlign = "center";
          oc.textBaseline = "middle";
          oc.fillText(
            t.name.charAt(0).toUpperCase(),
            ox + refSize / 2,
            oy + refSize / 2,
          );
        }
        oc.restore();

        // HP bar (V004: respect hpBarVisibility setting)
        const hpVisCached = hpBarVisibilityRef.current;
        const showHpCached = t.maxHp > 0 && (hpVisCached === 'always' || (hpVisCached === 'gm-only' && isGM));
        if (showHpCached) {
          const barW = refSize * 0.8;
          const barH = 5;
          const barX = ox + (refSize - barW) / 2;
          const barY = oy + refSize + 3;
          const ratio = clamp(t.hp / t.maxHp, 0, 1);
          oc.fillStyle = "rgba(0,0,0,0.5)";
          oc.fillRect(barX, barY, barW, barH);
          oc.fillStyle =
            ratio > 0.5 ? "#2ecc71" : ratio > 0.25 ? "#f39c12" : "#e74c3c";
          oc.fillRect(barX, barY, barW * ratio, barH);
          oc.strokeStyle = "rgba(255,255,255,0.3)";
          oc.lineWidth = 1;
          oc.strokeRect(barX, barY, barW, barH);
        }

        // Name (centered in full canvas width — no clipping)
        oc.fillStyle = "#fff";
        oc.font = nameFont;
        oc.textAlign = "center";
        oc.textBaseline = "top";
        oc.fillText(t.name, totalW / 2, oy + refSize + 10);

        // Unconscious/Dead visual overlays (rendered in offscreen cache).
        // Conditions are normalized to the canonical key — legacy tokens may
        // still carry RU labels / lowercase ids set before unification.
        const normalizedConds = t.conditions.map(normalizeConditionName);
        if (normalizedConds.includes("Dead")) {
          // Dead: grayscale + red X overlay
          oc.save();
          oc.globalAlpha = 0.4;
          oc.fillStyle = "#333";
          oc.fillRect(ox, oy, refSize, refSize);
          oc.globalAlpha = 0.8;
          oc.strokeStyle = "#e74c3c";
          oc.lineWidth = Math.max(3, refSize * 0.06);
          oc.lineCap = "round";
          const m = refSize * 0.2;
          oc.beginPath();
          oc.moveTo(ox + m, oy + m);
          oc.lineTo(ox + refSize - m, oy + refSize - m);
          oc.moveTo(ox + refSize - m, oy + m);
          oc.lineTo(ox + m, oy + refSize - m);
          oc.stroke();
          oc.restore();
        } else if (normalizedConds.includes("Unconscious")) {
          // Unconscious: semi-transparent overlay
          oc.save();
          oc.globalAlpha = 0.4;
          oc.fillStyle = "#000";
          oc.fillRect(ox, oy, refSize, refSize);
          oc.restore();
        }

        // Conditions
        if (t.conditions.length > 0) {
          oc.font = `${Math.max(8, 9)}px sans-serif`;
          oc.fillStyle = "#f39c12";
          const condText = normalizedConds.map((c) => (DND5E_CONDITION_LABELS_RU[c] ?? c).substring(0, 3)).join(" ");
          oc.fillText(condText, totalW / 2, oy - 10);
        }

        cached = {
          canvas: offCanvas,
          key: cacheKey,
          logicalW: totalW,
          logicalH: totalH,
          offsetX: ox,
        };
        tokenCacheMap.current.set(t.id, cached);
      }

      // Draw from cache, scaling from reference zoom to current zoom via drawImage
      const scale = cam.zoom / TOKEN_CACHE_ZOOM;
      const drawW = cached.logicalW * scale;
      const drawH = cached.logicalH * scale;
      const cacheOx = cached.offsetX;
      // Hidden tokens: GM sees them at 40% opacity (ghost view)
      const isHidden = !t.visible;
      if (isHidden) {
        ctx.save();
        ctx.globalAlpha = 0.4;
      }
      ctx.drawImage(
        cached.canvas,
        sx - cacheOx * scale,
        sy - (padding / 2) * scale,
        drawW,
        drawH,
      );

      // Selection indicator (drawn on main ctx, not cached — changes frequently)
      if (selIds.has(t.id)) {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 3]);
        ctx.strokeRect(sx - 4, sy - 4, size + 8, size + 8);
        ctx.setLineDash([]);
      }
      // HP bar on hover — drawn live on the main ctx, NOT the cache (hover
      // changes without a cache-key change). 'always'/'gm-only' bars are
      // baked into the cached sprite above; this covers the DEFAULT 'hover'
      // mode, which previously rendered no HP bar at all.
      if (
        hpBarVisibilityRef.current === 'hover' &&
        hoverTokenIdRef.current === t.id &&
        t.maxHp > 0
      ) {
        const barW = size * 0.8;
        const barH = Math.max(3, 4 * cam.zoom);
        const barX = sx + (size - barW) / 2;
        const barY = sy + size + 1;
        const ratio = clamp(t.hp / t.maxHp, 0, 1);
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fillRect(barX, barY, barW, barH);
        ctx.fillStyle = ratio > 0.5 ? "#2ecc71" : ratio > 0.25 ? "#f39c12" : "#e74c3c";
        ctx.fillRect(barX, barY, barW * ratio, barH);
      }
      if (isHidden) ctx.restore();

      // AoE affected glow (pulsing colored border matching template color)
      const glowColor = aoeAffectedRef.current.get(t.id);
      if (glowColor) {
        ctx.save();
        const pulseAlpha = Math.sin(now * 0.003) * 0.5 + 0.5;
        // Extract opaque color from rgba for glow (strip alpha)
        const opaqueColor = glowColor.replace(/[\d.]+\)$/, "1)");
        ctx.globalAlpha = 0.3 + 0.5 * pulseAlpha;
        ctx.strokeStyle = opaqueColor;
        ctx.lineWidth = 3;
        ctx.shadowColor = opaqueColor;
        ctx.shadowBlur = 12;
        const cx = sx + size / 2;
        const cy = sy + size / 2;
        const r = size / 2 + 4;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Continuous re-render for animated tokens
    if (hasAnimated) {
      if (!tokenAnimRAF.current) {
        const loop = () => {
          engine.markDirty('token');
          tokenAnimRAF.current = requestAnimationFrame(loop);
        };
        tokenAnimRAF.current = requestAnimationFrame(loop);
      }
    } else if (tokenAnimRAF.current) {
      cancelAnimationFrame(tokenAnimRAF.current);
      tokenAnimRAF.current = 0;
    }
  }, [engine, tokens.tokensRef, tokens.selectedTokenIdsRef, isGM]);

  // Render note pins on the token canvas (after tokens)
  const renderNotes = useCallback(() => {
    const canvas = engine.refs.current.token;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const cam = engine.cameraRef.current;
    const PIN_SIZE = 24; // Fixed screen-space size

    for (const note of notes) {
      if (!note || note.x == null) continue;
      const sx = note.x * cam.zoom + cam.offsetX;
      const sy = note.y * cam.zoom + cam.offsetY;

      ctx.save();

      // GM-only pins: semi-transparent with dashed border
      if (note.visibility === "gm") {
        ctx.globalAlpha = 0.6;
      }

      // Pin background circle
      ctx.beginPath();
      ctx.arc(sx, sy, PIN_SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = note.color;
      ctx.fill();

      // Border
      if (note.visibility === "gm") {
        ctx.setLineDash([3, 3]);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.8)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);

      // Icon emoji
      ctx.globalAlpha = 1;
      ctx.font = `${PIN_SIZE * 0.55}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#fff";
      const emoji = NOTE_ICON_MAP[note.icon] ?? "📌";
      ctx.fillText(emoji, sx, sy);

      ctx.restore();
    }
  }, [engine, notes]);

  // Render map pins on the token canvas (after notes)
  const renderPins = useCallback(() => {
    const canvas = engine.refs.current.token;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const cam = engine.cameraRef.current;
    const PIN_SIZE = 24;

    for (const pin of pins) {
      if (!pin || pin.x == null) continue;
      const sx = pin.x * cam.zoom + cam.offsetX;
      const sy = pin.y * cam.zoom + cam.offsetY;

      ctx.save();

      // GM-only pins: semi-transparent with dashed border
      if (pin.visibility === "gm") {
        ctx.globalAlpha = 0.6;
      } else if (pin.visibility === "owner") {
        ctx.globalAlpha = 0.8;
      }

      // Selection highlight
      const isSelected = selectionCtx.selection?.type === 'note' && selectionCtx.selection.noteId === pin.id;
      if (isSelected) {
        ctx.beginPath();
        ctx.arc(sx, sy, PIN_SIZE / 2 + 4, 0, Math.PI * 2);
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Pin background circle
      ctx.beginPath();
      ctx.arc(sx, sy, PIN_SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = pin.color;
      ctx.fill();

      // Border
      if (pin.visibility === "gm") {
        ctx.setLineDash([3, 3]);
      } else if (pin.visibility === "owner") {
        ctx.setLineDash([5, 3]);
      }
      ctx.strokeStyle = "rgba(255,255,255,0.8)";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);

      // Orphan indicator (no journalEntryId)
      if (!pin.journalEntryId) {
        ctx.beginPath();
        ctx.arc(
          sx + PIN_SIZE / 2 - 3,
          sy - PIN_SIZE / 2 + 3,
          4,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = "#ff6b35";
        ctx.fill();
      }

      // Icon emoji
      ctx.globalAlpha = 1;
      ctx.font = `${PIN_SIZE * 0.55}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#fff";
      const emoji = NOTE_ICON_MAP[pin.icon] ?? "📌";
      ctx.fillText(emoji, sx, sy);

      ctx.restore();
    }
  }, [engine, pins, selectionCtx.selection]);

  // Render AoE templates on UI layer (placed + preview)
  const aoePlacementRef = useRef(aoePlacement);
  aoePlacementRef.current = aoePlacement;

  const renderAoe = useCallback(() => {
    const hasTemplates = aoeTemplates && aoeTemplates.size > 0;
    const preview =
      aoePlacementRef.current?.state !== "idle"
        ? aoePlacementRef.current
        : null;
    if (!hasTemplates && !preview) return;

    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const cam = engine.cameraRef.current;
    const gs = engine.gridSize;

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(cam.offsetX, cam.offsetY);
    ctx.scale(cam.zoom, cam.zoom);

    // Render placed templates
    if (aoeTemplates && aoeTemplates.size > 0) {
      renderAoeTemplates(ctx, aoeTemplates, gs, GRID_SIZE_FT);
    }

    // Render placement preview (semi-transparent, dashed outline)
    if (preview) {
      const pt = preview?.previewTemplate;
      if (pt) {
        const previewMap = new Map<
          string,
          import("@roll1/shared").AoeTemplate
        >();
        previewMap.set("__preview__", {
          id: "__preview__",
          placedBy: "",
          ...pt,
        });
        ctx.globalAlpha = 0.5;
        renderAoeTemplates(ctx, previewMap, gs, GRID_SIZE_FT);
        ctx.globalAlpha = 1;
      }
    }

    ctx.restore();
  }, [engine, aoeTemplates, aoePlacement]);

  // Render marquee selection rectangle + link drawing preview on UI canvas
  const renderSelectionRect = useCallback(() => {
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const uiDpr = window.devicePixelRatio || 1;
    ctx.setTransform(uiDpr, 0, 0, uiDpr, 0, 0);
    const uiCssW = canvas.width / uiDpr;
    const uiCssH = canvas.height / uiDpr;
    ctx.clearRect(0, 0, uiCssW, uiCssH);

    // Marquee selection
    const r = selectionRect.current;
    if (r) {
      const cam = engine.cameraRef.current;
      const sx1 = r.x1 * cam.zoom + cam.offsetX;
      const sy1 = r.y1 * cam.zoom + cam.offsetY;
      const sx2 = r.x2 * cam.zoom + cam.offsetX;
      const sy2 = r.y2 * cam.zoom + cam.offsetY;
      ctx.save();
      ctx.strokeStyle = "rgba(100, 180, 255, 0.8)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(sx1, sy1, sx2 - sx1, sy2 - sy1);
      ctx.fillStyle = "rgba(100, 180, 255, 0.1)";
      ctx.fillRect(sx1, sy1, sx2 - sx1, sy2 - sy1);
      ctx.restore();
    }

    // Link drawing preview (rect/circle drag, polygon points)
    const cam = engine.cameraRef.current;
    const start = linkDrawStart.current;
    if (currentTool === "location-link" && start) {
      ctx.save();
      ctx.strokeStyle = "rgba(99, 102, 241, 0.8)";
      ctx.fillStyle = "rgba(99, 102, 241, 0.15)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);

      if (linkDrawMode === "circle") {
        // Preview circle from center to current drag position
        const sx = start.x * cam.zoom + cam.offsetX;
        const sy = start.y * cam.zoom + cam.offsetY;
        const endX = selectionRect.current?.x2 ?? start.x;
        const endY = selectionRect.current?.y2 ?? start.y;
        const dx = endX - start.x;
        const dy = endY - start.y;
        const radius = Math.sqrt(dx * dx + dy * dy) * cam.zoom;
        ctx.beginPath();
        ctx.arc(sx, sy, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        // Preview rect
        const sx = start.x * cam.zoom + cam.offsetX;
        const sy = start.y * cam.zoom + cam.offsetY;
        const endX = selectionRect.current?.x2 ?? start.x;
        const endY = selectionRect.current?.y2 ?? start.y;
        const sw = (endX - start.x) * cam.zoom;
        const sh = (endY - start.y) * cam.zoom;
        ctx.beginPath();
        ctx.rect(sx, sy, sw, sh);
        ctx.fill();
        ctx.stroke();
      }

      ctx.restore();
    }

    // Polygon preview
    if (
      currentTool === "location-link" &&
      linkDrawMode === "polygon" &&
      linkPolygonPoints.current.length > 0
    ) {
      const pts = linkPolygonPoints.current;
      const cursorWorld = lastWorldPos.current;
      const cursorSx = cursorWorld.x * cam.zoom + cam.offsetX;
      const cursorSy = cursorWorld.y * cam.zoom + cam.offsetY;
      const firstSx = pts[0].x * cam.zoom + cam.offsetX;
      const firstSy = pts[0].y * cam.zoom + cam.offsetY;
      const distToFirst = Math.hypot(cursorSx - firstSx, cursorSy - firstSy);
      const canSnap = pts.length >= 3 && distToFirst < 15;

      ctx.save();
      ctx.strokeStyle = "rgba(99, 102, 241, 0.8)";
      ctx.fillStyle = "rgba(99, 102, 241, 0.15)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.beginPath();
      ctx.moveTo(firstSx, firstSy);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(
          pts[i].x * cam.zoom + cam.offsetX,
          pts[i].y * cam.zoom + cam.offsetY,
        );
      }
      ctx.stroke();

      // Preview line from last point to cursor
      const lastPt = pts[pts.length - 1];
      const lastSx = lastPt.x * cam.zoom + cam.offsetX;
      const lastSy = lastPt.y * cam.zoom + cam.offsetY;
      ctx.strokeStyle = "rgba(99, 102, 241, 0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(lastSx, lastSy);
      ctx.lineTo(cursorSx, cursorSy);
      ctx.stroke();

      // Draw dots at each vertex
      ctx.setLineDash([]);
      for (const pt of pts) {
        ctx.beginPath();
        ctx.arc(
          pt.x * cam.zoom + cam.offsetX,
          pt.y * cam.zoom + cam.offsetY,
          4,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = "rgba(99, 102, 241, 0.9)";
        ctx.fill();
      }

      // Snap-to-first-point visual indicator
      if (canSnap) {
        ctx.beginPath();
        ctx.arc(firstSx, firstSy, 8, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.stroke();
        ctx.fillStyle = "rgba(99, 102, 241, 0.6)";
        ctx.fill();
      }

      ctx.restore();
    }

    // Map-object drawing preview (rect/circle drag, polygon points)
    const moStart = mapObjDrawStart.current;
    if (currentTool === "map-object" && moStart) {
      ctx.save();
      ctx.strokeStyle = "rgba(139, 92, 246, 0.8)";
      ctx.fillStyle = "rgba(139, 92, 246, 0.15)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);

      if (mapObjectDrawMode === "circle") {
        const sx = moStart.x * cam.zoom + cam.offsetX;
        const sy = moStart.y * cam.zoom + cam.offsetY;
        const endX = selectionRect.current?.x2 ?? moStart.x;
        const endY = selectionRect.current?.y2 ?? moStart.y;
        const dx = endX - moStart.x;
        const dy = endY - moStart.y;
        const radius = Math.sqrt(dx * dx + dy * dy) * cam.zoom;
        ctx.beginPath();
        ctx.arc(sx, sy, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        const sx = moStart.x * cam.zoom + cam.offsetX;
        const sy = moStart.y * cam.zoom + cam.offsetY;
        const endX = selectionRect.current?.x2 ?? moStart.x;
        const endY = selectionRect.current?.y2 ?? moStart.y;
        const sw = (endX - moStart.x) * cam.zoom;
        const sh = (endY - moStart.y) * cam.zoom;
        ctx.beginPath();
        ctx.rect(sx, sy, sw, sh);
        ctx.fill();
        ctx.stroke();
      }

      ctx.restore();
    }

    // Map-object polygon preview
    if (
      currentTool === "map-object" &&
      mapObjectDrawMode === "polygon" &&
      mapObjPolygonPoints.current.length > 0
    ) {
      const pts = mapObjPolygonPoints.current;
      const cursorWorld = lastWorldPos.current;
      const cursorSx = cursorWorld.x * cam.zoom + cam.offsetX;
      const cursorSy = cursorWorld.y * cam.zoom + cam.offsetY;
      const firstSx = pts[0].x * cam.zoom + cam.offsetX;
      const firstSy = pts[0].y * cam.zoom + cam.offsetY;
      const distToFirst = Math.hypot(cursorSx - firstSx, cursorSy - firstSy);
      const canSnap = pts.length >= 3 && distToFirst < 15;

      ctx.save();
      ctx.strokeStyle = "rgba(139, 92, 246, 0.8)";
      ctx.fillStyle = "rgba(139, 92, 246, 0.15)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.beginPath();
      ctx.moveTo(firstSx, firstSy);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(
          pts[i].x * cam.zoom + cam.offsetX,
          pts[i].y * cam.zoom + cam.offsetY,
        );
      }
      ctx.stroke();

      const lastPt = pts[pts.length - 1];
      const lastSx = lastPt.x * cam.zoom + cam.offsetX;
      const lastSy = lastPt.y * cam.zoom + cam.offsetY;
      ctx.strokeStyle = "rgba(139, 92, 246, 0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(lastSx, lastSy);
      ctx.lineTo(cursorSx, cursorSy);
      ctx.stroke();

      ctx.setLineDash([]);
      for (const pt of pts) {
        ctx.beginPath();
        ctx.arc(
          pt.x * cam.zoom + cam.offsetX,
          pt.y * cam.zoom + cam.offsetY,
          4,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = "rgba(139, 92, 246, 0.9)";
        ctx.fill();
      }

      if (canSnap) {
        ctx.beginPath();
        ctx.arc(firstSx, firstSy, 8, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.stroke();
        ctx.fillStyle = "rgba(139, 92, 246, 0.6)";
        ctx.fill();
      }

      ctx.restore();
    }

    // Drawing selection overlay
    if (currentTool === "select-drawing" && drawingSelection) {
      const ds = drawingSelection.state.current;

      // Marquee rectangle
      if (ds.marqueeRect && ds.mode === "selecting") {
        const mr = ds.marqueeRect;
        const sx = mr.x * cam.zoom + cam.offsetX;
        const sy = mr.y * cam.zoom + cam.offsetY;
        const sw = mr.width * cam.zoom;
        const sh = mr.height * cam.zoom;
        ctx.save();
        ctx.fillStyle = "rgba(59, 130, 246, 0.1)";
        ctx.strokeStyle = "rgba(59, 130, 246, 0.5)";
        ctx.lineWidth = 1;
        ctx.fillRect(sx, sy, sw, sh);
        ctx.strokeRect(sx, sy, sw, sh);
        ctx.restore();
      }

      // Selection bounding box
      if (
        ds.selectionBounds &&
        (ds.selectedStrokeIds.size > 0 || ds.selectedNoteIds.size > 0)
      ) {
        const b = ds.selectionBounds;
        const pad = 4;
        const ox = ds.mode === "moving" ? ds.moveOffset.dx : 0;
        const oy = ds.mode === "moving" ? ds.moveOffset.dy : 0;
        const sx = (b.x - pad + ox) * cam.zoom + cam.offsetX;
        const sy = (b.y - pad + oy) * cam.zoom + cam.offsetY;
        const sw = (b.width + pad * 2) * cam.zoom;
        const sh = (b.height + pad * 2) * cam.zoom;
        ctx.save();
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = (2 / cam.zoom) * cam.zoom; // visual 2px regardless of zoom
        ctx.setLineDash([6, 3]);
        ctx.strokeRect(sx, sy, sw, sh);
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    // Fog tool previews
    if (currentTool === "fog" && fog) {
      const cursorWorld = lastWorldPos.current;
      const cursorSx = cursorWorld.x * cam.zoom + cam.offsetX;
      const cursorSy = cursorWorld.y * cam.zoom + cam.offsetY;

      // Brush preview circle
      if (!fog.fogMode.current || fog.fogMode.current === "brush") {
        const r = (fog.brushSize.current / 2) * cam.zoom;
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.arc(cursorSx, cursorSy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }

      // Rect preview
      if (fog.rectStart.current && fog.rectEnd.current) {
        const s = worldToScreen(
          fog.rectStart.current.x,
          fog.rectStart.current.y,
          cam,
        );
        const e2 = worldToScreen(
          fog.rectEnd.current.x,
          fog.rectEnd.current.y,
          cam,
        );
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,255,0.6)";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(s.x, s.y, e2.x - s.x, e2.y - s.y);
        ctx.setLineDash([]);
        ctx.restore();
      }

      // Polygon preview
      if (fog.polygonPoints.current.length > 0) {
        const pts = fog.polygonPoints.current.map((p) =>
          worldToScreen(p.x, p.y, cam),
        );
        ctx.save();
        ctx.strokeStyle = "rgba(255,255,255,0.6)";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.lineTo(cursorSx, cursorSy);
        ctx.stroke();
        ctx.setLineDash([]);
        for (const p of pts) {
          ctx.fillStyle = "rgba(255,255,255,0.8)";
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }
  }, [
    engine,
    currentTool,
    linkDrawMode,
    mapObjectDrawMode,
    drawingSelection,
    fog,
  ]);

  // Render inpaint selection overlay on UI layer (does NOT clear)
  const renderInpaintOverlay = useCallback(() => {
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const cam = engine.cameraRef.current;

    // Draw preview image if available
    if (inpainting?.previewUrl && inpaintPreviewImage.current?.complete) {
      const img = inpaintPreviewImage.current;
      // Draw preview over map area at map origin (0,0), accounting for mapScale
      const scale = engine.mapScaleRef.current;
      const sx = cam.offsetX;
      const sy = cam.offsetY;
      const sw = img.naturalWidth * cam.zoom * scale;
      const sh = img.naturalHeight * cam.zoom * scale;
      ctx.save();
      ctx.globalAlpha = 0.85;
      ctx.drawImage(img, sx, sy, sw, sh);
      ctx.restore();
    }

    // Draw active selection region
    const region: MaskRegion | null =
      inpaintRectStart.current && inpaintRectEnd.current
        ? {
            type: "rect" as const,
            x: Math.min(inpaintRectStart.current.x, inpaintRectEnd.current.x),
            y: Math.min(inpaintRectStart.current.y, inpaintRectEnd.current.y),
            width: Math.abs(inpaintRectEnd.current.x - inpaintRectStart.current.x),
            height: Math.abs(inpaintRectEnd.current.y - inpaintRectStart.current.y),
          }
        : inpaintLassoPoints.current.length >= 2
          ? { type: "lasso" as const, points: [...inpaintLassoPoints.current] }
          : inpainting?.region ?? null;

    if (!region) return;

    ctx.save();
    ctx.fillStyle = "rgba(0, 100, 255, 0.3)";
    ctx.strokeStyle = "#0064ff";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);

    if (region.type === "rect") {
      const rx = region.x * cam.zoom + cam.offsetX;
      const ry = region.y * cam.zoom + cam.offsetY;
      const rw = region.width * cam.zoom;
      const rh = region.height * cam.zoom;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
    } else {
      const pts = region.points;
      if (pts.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(
          pts[0].x * cam.zoom + cam.offsetX,
          pts[0].y * cam.zoom + cam.offsetY,
        );
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(
            pts[i].x * cam.zoom + cam.offsetX,
            pts[i].y * cam.zoom + cam.offsetY,
          );
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    }
    ctx.restore();
  }, [engine, inpainting?.region, inpainting?.previewUrl]);

  // Render detail-rect selection overlay on UI layer (does NOT clear)
  const renderDetailRectOverlay = useCallback(() => {
    if (!detailRectStart.current || !detailRectEnd.current) return;
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const cam = engine.cameraRef.current;
    const start = detailRectStart.current;
    const end = detailRectEnd.current;
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    const sx = x * cam.zoom + cam.offsetX;
    const sy = y * cam.zoom + cam.offsetY;
    const sw = w * cam.zoom;
    const sh = h * cam.zoom;

    ctx.save();
    ctx.setLineDash([8, 4]);
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 2;
    ctx.strokeRect(sx, sy, sw, sh);
    ctx.fillStyle = "rgba(59, 130, 246, 0.1)";
    ctx.fillRect(sx, sy, sw, sh);
    ctx.restore();
  }, [engine]);

  // Load preview image when previewUrl changes
  useEffect(() => {
    if (!inpainting?.previewUrl) {
      inpaintPreviewImage.current = null;
      return;
    }
    const img = new Image();
    img.onload = () => {
      inpaintPreviewImage.current = img;
      engine.markDirty("ui");
    };
    img.src = inpainting.previewUrl;
  }, [inpainting?.previewUrl, engine]);

  // Render ping markers on UI layer (does NOT clear — renderSelectionRect already cleared)
  function hexToRgb(hex: string): [number, number, number] {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  const renderPings = useCallback(() => {
    if (pingsRef.current.length === 0) return;
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const cam = engine.cameraRef.current;
    const now = Date.now();

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const ping of pingsRef.current) {
      const elapsed = now - ping.startTime;
      const progress = elapsed / 3000; // 3s duration
      const alpha = Math.max(0, 1 - progress);
      const radius = 12 + progress * 90; // expanding ring

      const sx = ping.x * cam.zoom + cam.offsetX;
      const sy = ping.y * cam.zoom + cam.offsetY;

      const [r, g, b] = hexToRgb(ping.color ?? '#ffc83c');

      // Outer pulsing ring
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.8})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Inner dot
      ctx.beginPath();
      ctx.arc(sx, sy, 5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
      ctx.fill();
    }
    ctx.restore();
  }, [engine]);

  // Wall overlay rendering (GM-only, UI layer)
  const wallsRef = canvasCtx.walls;
  const wallChainPreviewRef = canvasCtx.wallChainPreview;
  const renderWalls = useCallback(() => {
    if (!sessionCtx.isGM) return;
    if (!wallsRef) return;

    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const cam = engine.cameraRef.current;

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(cam.offsetX, cam.offsetY);
    ctx.scale(cam.zoom, cam.zoom);

    const wallList = wallsRef.current;
    const preview = wallChainPreviewRef.current;
    const hoveredId = preview.hoveredWallId;

    if (wallList && wallList.length > 0) {
      for (const wall of wallList) {
        const isHovered = wall.id === hoveredId;
        const style = getWallStyle(wall.blocking, wall.doorState);
        ctx.beginPath();
        ctx.moveTo(wall.x1, wall.y1);
        ctx.lineTo(wall.x2, wall.y2);
        ctx.strokeStyle = isHovered ? "#FFFFFF" : (wall.color ?? style.color);
        ctx.lineWidth = (style.lineWidth + (isHovered ? 2 : 0)) / cam.zoom;
        ctx.setLineDash(style.lineDash.map(d => d / cam.zoom));
        ctx.lineCap = "round";
        ctx.stroke();
        ctx.setLineDash([]);

        // Door indicator: small square/circle at midpoint
        if (wall.blocking === "door") {
          const mx = (wall.x1 + wall.x2) / 2;
          const my = (wall.y1 + wall.y2) / 2;
          const indicatorSize = 4 / cam.zoom;
          ctx.fillStyle = style.color;
          if (wall.doorState === "locked") {
            ctx.fillRect(mx - indicatorSize, my - indicatorSize, indicatorSize * 2, indicatorSize * 2);
          } else {
            ctx.beginPath();
            ctx.arc(mx, my, indicatorSize, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    // Chain preview (rubber-band lines)
    const pts = preview.points;
    if (pts.length > 0) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i].x, pts[i].y);
      }
      if (preview.previewPoint) {
        ctx.lineTo(preview.previewPoint.x, preview.previewPoint.y);
      }
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2 / cam.zoom;
      ctx.setLineDash([6 / cam.zoom, 4 / cam.zoom]);
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw vertices
      for (const pt of pts) {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 3 / cam.zoom, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
      }
    }

    // Snap indicator: highlight endpoint when cursor snaps
    if (preview.snapPoint) {
      const sp = preview.snapPoint;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 6 / cam.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = "#00FF88";
      ctx.lineWidth = 2 / cam.zoom;
      ctx.stroke();
    }

    ctx.restore();
  }, [engine, sessionCtx.isGM, wallsRef, wallChainPreviewRef]);

  // Register render callbacks with layer bindings
  useEffect(() => {
    const cbs = engine.renderCallbacks.current;
    cbs.length = 0;
    cbs.push(
      { layer: "token", render: renderTokens },
      { layer: "token", render: renderNotes },
      { layer: "token", render: renderPins },
      { layer: "drawing", render: drawing.renderDrawing },
      { layer: "fog", render: fog.renderFog },
      { layer: "ui", render: renderSelectionRect },
      { layer: "ui", render: renderAoe },
      { layer: "ui", render: renderPings },
      { layer: "ui", render: renderInpaintOverlay },
      { layer: "ui", render: renderDetailRectOverlay },
      ...(locLinks
        ? [{ layer: "ui" as const, render: locLinks.renderLocationLinks }]
        : []),
      ...(mapObjectsLocal
        ? [{ layer: "ui" as const, render: mapObjectsLocal.renderMapObjects }]
        : []),
      ...(sessionCtx.isGM
        ? [{ layer: "ui" as const, render: renderWalls }]
        : []),
      // AI Action Preview ghost overlay (Spec D) — registered after renderSelectionRect
      ...(aiPreviewRender
        ? [{ layer: "ui" as const, render: aiPreviewRender }]
        : []),
    );
    engine.markAllDirty();
  }, [
    engine,
    renderTokens,
    renderNotes,
    renderPins,
    locLinks?.renderLocationLinks,
    mapObjectsLocal?.renderMapObjects,
    drawing.renderDrawing,
    fog.renderFog,
    renderAoe,
    renderPings,
    renderInpaintOverlay,
    renderDetailRectOverlay,
    renderSelectionRect,
    renderWalls,
    sessionCtx.isGM,
    aiPreviewRender,
  ]);

  // Also re-render token layer when tokens or notes change
  useEffect(() => {
    engine.markDirty("token");
  }, [tokens.tokens, tokens.selectedTokenIds, notes, pins, engine]);
  // Animate pulse auras and AoE glow — only redraw token layer, not all layers
  useEffect(() => {
    const hasPulse = tokens.tokens.some(
      (t) => t.visible && t.auras?.some((a) => a.style === "pulse"),
    );
    const hasAoeGlow = aoeAffectedTokenIds && aoeAffectedTokenIds.size > 0;
    if (!hasPulse && !hasAoeGlow) return;
    let raf: number;
    const animate = () => {
      engine.markDirty("token");
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [tokens.tokens, aoeAffectedTokenIds, engine]);

  // Pan/zoom setup — debounce fog/drawing cache invalidation so caches survive rapid zoom
  const zoomCacheTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const ui = engine.refs.current.ui;
    if (!ui) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = ui.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const oldCam = engine.cameraRef.current;
      const delta = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
      const newZoom = clamp(oldCam.zoom * delta, 0.1, 5);
      engine.setCamera({
        zoom: newZoom,
        offsetX: mx - (mx - oldCam.offsetX) * (newZoom / oldCam.zoom),
        offsetY: my - (my - oldCam.offsetY) * (newZoom / oldCam.zoom),
      });
      // Mark all layers dirty — fog/drawing will use fast transform path (no cache rebuild)
      // Token cache is zoom-independent, so no invalidation needed
      engine.markAllDirty();

      // Debounce fog/drawing cache invalidation — rebuild pixel-perfect only after zoom stops
      if (zoomCacheTimerRef.current) clearTimeout(zoomCacheTimerRef.current);
      zoomCacheTimerRef.current = setTimeout(() => {
        zoomCacheTimerRef.current = null;
        fog.invalidateCache();
        drawing.invalidateCache();
        engine.markDirty("fog");
        engine.markDirty("drawing");
      }, 150);
    };

    ui.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      ui.removeEventListener("wheel", handleWheel);
      if (zoomCacheTimerRef.current) {
        clearTimeout(zoomCacheTimerRef.current);
        zoomCacheTimerRef.current = null;
      }
    };
  }, [engine, fog, drawing]);

  // Space key for panning; Escape to cancel measure; Q key for waypoints
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.code === "Space" && !e.repeat) {
        spaceDown.current = true;
        const ui = engine.refs.current.ui;
        if (ui) ui.style.cursor = "grab";
        e.preventDefault();
      }
      // Delete/Backspace: delete selected MapObject
      if (
        (e.code === "Delete" || e.code === "Backspace") &&
        currentTool === "select" &&
        mapObjectsLocal?.selectedObjectId.current
      ) {
        const objectId = mapObjectsLocal.selectedObjectId.current;
        mapObjectsLocal.selectedObjectId.current = null;
        engine.markDirty("ui");
        get("map-object:delete")?.(objectId);
        e.preventDefault();
      }
      // Delete/Backspace: delete selected drawing strokes
      if (
        (e.code === "Delete" || e.code === "Backspace") &&
        currentTool === "select-drawing" &&
        drawingSelection
      ) {
        const ids = drawingSelection.getSelectedIds();
        const noteIds = drawingSelection.getSelectedNoteIds();
        if (ids.length > 0 || noteIds.length > 0) {
          const deleted =
            ids.length > 0 ? drawingSelection.deleteLocal(ids) : [];
          drawingSelection.clearSelection();
          get("drawing:delete-end")?.(ids, deleted, noteIds);
          e.preventDefault();
        }
      }
      // Enter key: finalize polygon portal
      if (
        e.code === "Enter" &&
        currentTool === "location-link" &&
        linkDrawMode === "polygon" &&
        linkPolygonPoints.current.length >= 3
      ) {
        get("link:drawn")?.({
          type: "polygon",
          points: [...linkPolygonPoints.current],
        });
        linkPolygonPoints.current = [];
        engine.markDirty("ui");
        e.preventDefault();
      }
      // Enter key: finalize map-object polygon
      if (
        e.code === "Enter" &&
        currentTool === "map-object" &&
        mapObjectDrawMode === "polygon" &&
        mapObjPolygonPoints.current.length >= 3
      ) {
        get("map-object:drawn")?.({
          type: "polygon",
          points: [...mapObjPolygonPoints.current],
        });
        mapObjPolygonPoints.current = [];
        engine.markDirty("ui");
        e.preventDefault();
      }
      if (e.code === "Escape") {
        // Clear drawing selection
        if (currentTool === "select-drawing" && drawingSelection) {
          drawingSelection.clearSelection();
        }
        // Cancel any active measurement
        drawing.endMeasure();
        measureActive.current = false;
        measureDownScreenPos.current = null;
        // Dismiss persistent public rulers (right-click or Escape)
        drawing.clearPublicRulers();
        // Cancel polygon drawing
        if (linkPolygonPoints.current.length > 0) {
          linkPolygonPoints.current = [];
          engine.markDirty("ui");
        }
        // Cancel map-object polygon drawing
        if (mapObjPolygonPoints.current.length > 0) {
          mapObjPolygonPoints.current = [];
          engine.markDirty("ui");
        }
        // Cancel rect/circle drawing
        if (linkDrawStart.current) {
          linkDrawStart.current = null;
          selectionRect.current = null;
          engine.markDirty("ui");
        }
        // Cancel map-object rect/circle drawing
        if (mapObjDrawStart.current) {
          mapObjDrawStart.current = null;
          selectionRect.current = null;
          engine.markDirty("ui");
        }
        // Cancel wall chain drawing
        if (currentTool === "wall") {
          get("wall:cancel-chain")?.();
        }
      }
      // Q key: add waypoint at current cursor position during measurement
      // Works both in click-waypoint mode (measureActive) and during drag (isMouseDown)
      if (
        e.code === "KeyQ" &&
        currentTool === "measure" &&
        (measureActive.current || isMouseDown.current)
      ) {
        const pos = lastWorldPos.current;
        drawing.addWaypoint(pos.x, pos.y);
        // If we were dragging, switch to waypoint mode so releasing mouse doesn't end measurement
        if (isMouseDown.current && !measureActive.current) {
          measureActive.current = true;
        }
      }
      // Q key: pin a waypoint on the token drag ruler → polyline movement path
      // (same key as the measure tool; here it works while dragging a token).
      if (
        e.code === "KeyQ" &&
        currentTool === "select" &&
        isMouseDown.current &&
        tokenDragRulerStarted.current &&
        dragTokens.current?.items.length
      ) {
        const dt0 = dragTokens.current.items[0];
        drawing.addTokenDragWaypoint(dt0.token.x, dt0.token.y);
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        spaceDown.current = false;
        const ui = engine.refs.current.ui;
        if (ui) ui.style.cursor = "";
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [engine, drawing, currentTool, mapObjectsLocal]);

  const getWorldPos = useCallback(
    (e: React.MouseEvent) => {
      const { engine, snapToGrid } = d.current;
      const w = engine.mouseToWorld(e.nativeEvent);
      if (snapToGrid) {
        const s = engine.snapToGridCenter(w.x, w.y);
        return { raw: w, snapped: s };
      }
      return { raw: w, snapped: w };
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.PointerEvent) => {
      const { engine, currentTool, tokens, drawing, fog, isGM, mapObjects: mapObjectsLocal, selectionCtx, sessionCtx, pageId, drawingSelection, locLinks, linkDrawMode, mapObjectDrawMode, get } = d.current;
      if (e.button === 1 || (e.button === 0 && spaceDown.current)) {
        isPanning.current = true;
        panStart.current = {
          x: e.clientX,
          y: e.clientY,
          ox: engine.camera.offsetX,
          oy: engine.camera.offsetY,
        };
        const ui = engine.refs.current.ui;
        if (ui) ui.style.cursor = "grabbing";
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        e.preventDefault();
        return;
      }
      if (e.button !== 0) return;
      isMouseDown.current = true;
      didDragRef.current = false;
      clickedTokenRef.current = null;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const { raw, snapped } = getWorldPos(e);

      // Long-press → ping (any tool, 500ms hold without drag)
      if (pingLongPressTimer.current) clearTimeout(pingLongPressTimer.current);
      pingLongPressPos.current = { x: raw.x, y: raw.y };
      pingLongPressTimer.current = window.setTimeout(() => {
        pingLongPressTimer.current = 0;
        const pos = pingLongPressPos.current;
        if (!pos) return;
        addPing(pos.x, pos.y, d.current.sessionCtx.mp.participant?.id ?? 'local');
        if (d.current.sessionCtx.mp.session) {
          d.current.sessionCtx.mp.send({ type: 'ping:show', x: pos.x, y: pos.y } as any);
        }
        pingLongPressPos.current = null;
      }, 500);

      switch (currentTool) {
        case "select": {
          // Hit-test priority: token > mapObject > pin > drawing > text > locationLink
          const t = tokens.getTokenAt(raw.x, raw.y, tokenHitFilter);
          if (t) {
            // Auth: players can only drag assigned or controlled tokens —
            // unless the GM granted playerPermissions.moveOtherTokens (mirrors
            // the server grant in tokenMoveHandler; the client gate must match
            // or the drag is blocked before the command is even sent).
            if (!isGM) {
              const myAssigned =
                sessionCtx.mp.participant?.assignedTokenIds ?? [];
              const controlled = canvasCtx.controlledTokenIdsRef.current;
              const mayMoveOthers =
                sessionCtx.sessionSettings?.playerPermissions?.moveOtherTokens === true;
              if (!mayMoveOthers && !myAssigned.includes(t.id) && !controlled.has(t.id)) {
                // Click still selects for inspection, but no drag
                tokens.selectToken(t.id);
                selectionCtx.select({ type: 'token', tokenId: t.id, pageId });
                // Token clicked but not assigned — selected for inspection only
                break;
              }
            }
            clickedTokenRef.current = t;
            // Store original position for drag ruler
            dragTokenOrigin.current = { x: t.x, y: t.y };
            // If clicking an already-selected token in a multi-selection, drag all
            if (
              tokens.selectedTokenIds.has(t.id) &&
              tokens.selectedTokenIds.size > 1
            ) {
              dragTokens.current = {
                items: tokens.tokens
                  .filter((tok) => tokens.selectedTokenIds.has(tok.id))
                  .map((tok) => ({
                    token: tok,
                    offsetX: tok.x - raw.x,
                    offsetY: tok.y - raw.y,
                    origX: tok.x,
                    origY: tok.y,
                  })),
              };
            } else {
              tokens.selectToken(t.id);
              selectionCtx.select({ type: 'token', tokenId: t.id, pageId });
              dragTokens.current = {
                items: [
                  {
                    token: t,
                    offsetX: t.x - raw.x,
                    offsetY: t.y - raw.y,
                    origX: t.x,
                    origY: t.y,
                  },
                ],
              };
            }
          } else {
            // Check for MapObject under cursor (GM only — select/drag)
            const mo =
              isGM && mapObjectsLocal
                ? mapObjectsLocal.getObjectAtWorld(raw.x, raw.y)
                : null;
            if (mo) {
              mapObjectsLocal!.selectedObjectId.current = mo.id;
              selectionCtx.select({ type: 'mapObject', objectId: mo.id, pageId });
              get("map-object:select")?.(mo.id);
              engine.markDirty("ui");
              // Start drag
              dragMapObject.current = {
                objectId: mo.id,
                origRegion: JSON.parse(JSON.stringify(mo.region)),
                startX: raw.x,
                startY: raw.y,
              };
            } else {
              // Check for pin under cursor (MapPin hit-test)
              const pin = getPinAtWorld(raw.x, raw.y);
              console.warn('[select] pin hit-test:', { found: !!pin, pinId: pin?.id?.slice(0,6), click: { x: raw.x, y: raw.y }, dist: pin ? Math.hypot(raw.x - pin.x, raw.y - pin.y) : null, hitRadius: Math.max(20, 14 / engine.camera.zoom) });
              if (pin) {
                selectionCtx.select({ type: 'note', noteId: pin.id, pageId });
                // Only allow drag for GM or pin owner
                const myId = sessionCtx.mp.participant?.id;
                if (isGM || pin.createdBy === myId) {
                  dragPin.current = {
                    pinId: pin.id,
                    offsetX: pin.x - raw.x,
                    offsetY: pin.y - raw.y,
                  };
                }
              } else {
                // Check for legacy note under cursor for dragging
                const n = getNoteAtWorld(raw.x, raw.y);
                if (n) {
                  selectionCtx.select({ type: 'note', noteId: n.id, pageId });
                  dragNote.current = {
                    note: { ...n },
                    offsetX: n.x - raw.x,
                    offsetY: n.y - raw.y,
                  };
                } else if (drawingSelection) {
                  // Check for drawing stroke under cursor
                  const drawHit = drawingSelection.selectAt(raw.x, raw.y);
                  if (drawHit) {
                    const ids = drawingSelection.getSelectedIds();
                    if (ids.length > 0) {
                      selectionCtx.select({ type: 'drawing', strokeId: ids[0], pageId });
                    }
                  } else {
                    // Check for text object under cursor
                    const textHit = get("text:get-at-world")?.(raw.x, raw.y);
                    if (textHit) {
                      selectionCtx.select({ type: 'text', textId: textHit.id, pageId });
                    } else {
                      // Check for location link under cursor
                      const link = locLinks?.getLinkAtWorld(raw.x, raw.y) ?? null;
                      if (link && (locLinks!.isLinkVisible(link) || isGM)) {
                        selectionCtx.select({ type: 'locationLink', linkId: link.id, pageId });
                      } else {
                        // Empty area: clear everything
                        tokens.selectToken(null);
                        selectionCtx.clearSelection();
                        // Deselect map object when clicking empty space
                        if (mapObjectsLocal && mapObjectsLocal.selectedObjectId.current) {
                          mapObjectsLocal.selectedObjectId.current = null;
                          engine.markDirty("ui");
                        }
                        // Clear drawing selection
                        if (drawingSelection) {
                          drawingSelection.clearSelection();
                        }
                        // Start marquee selection on empty space
                        selectionRect.current = {
                          x1: raw.x,
                          y1: raw.y,
                          x2: raw.x,
                          y2: raw.y,
                        };
                      }
                    }
                  }
                } else {
                  // No drawingSelection available — check text and links
                  const textHit = get("text:get-at-world")?.(raw.x, raw.y);
                  if (textHit) {
                    selectionCtx.select({ type: 'text', textId: textHit.id, pageId });
                  } else {
                    const link = locLinks?.getLinkAtWorld(raw.x, raw.y) ?? null;
                    if (link && (locLinks!.isLinkVisible(link) || isGM)) {
                      selectionCtx.select({ type: 'locationLink', linkId: link.id, pageId });
                    } else {
                      // Empty area: clear everything
                      tokens.selectToken(null);
                      selectionCtx.clearSelection();
                      if (mapObjectsLocal && mapObjectsLocal.selectedObjectId.current) {
                        mapObjectsLocal.selectedObjectId.current = null;
                        engine.markDirty("ui");
                      }
                      selectionRect.current = {
                        x1: raw.x,
                        y1: raw.y,
                        x2: raw.x,
                        y2: raw.y,
                      };
                    }
                  }
                }
              }
            }
          }
          break;
        }
        case "select-drawing": {
          if (drawingSelection) {
            // Check if clicking on current selection (start move)
            if (
              drawingSelection.state.current.selectedStrokeIds.size > 0 &&
              drawingSelection.isInSelection(raw.x, raw.y)
            ) {
              drawingSelection.startMove(raw.x, raw.y);
            } else {
              // Try hit-test for single click selection
              const hit = drawingSelection.selectAt(raw.x, raw.y);
              if (hit) {
                const ids = drawingSelection.getSelectedIds();
                if (ids.length > 0) {
                  selectionCtx.select({ type: 'drawing', strokeId: ids[0], pageId });
                }
              } else {
                // Start marquee selection
                drawingSelection.startMarquee(raw.x, raw.y);
                selectionCtx.clearSelection();
              }
            }
          }
          break;
        }
        case "draw":
        case "erase":
          drawing.startStroke(snapped.x, snapped.y, currentTool);
          break;
        case "shape":
          drawing.startStroke(snapped.x, snapped.y, "shape");
          break;
        case "measure":
          measureDownScreenPos.current = { x: e.clientX, y: e.clientY };
          if (!measureActive.current) {
            drawing.startMeasure(snapped.x, snapped.y, e.shiftKey);
          }
          break;
        case "fog":
          if (fog.fogMode.current === "rect") {
            fog.startRectReveal(snapped.x, snapped.y);
          } else if (fog.fogMode.current === "polygon") {
            fog.addPolygonPoint(snapped.x, snapped.y);
          } else {
            // brush mode
            if (fog.reFogMode.current) {
              fogCloseStartIdx.current = fog.closings.current.length;
              fog.startClose(snapped.x, snapped.y);
            } else {
              fogRevealStartIdx.current = fog.revealed.current.length;
              fog.startReveal(snapped.x, snapped.y);
            }
          }
          break;
        case "wall":
          get("wall:tool-click")?.(snapped.x, snapped.y);
          break;
        case "note":
          get("note:tool-click")?.(snapped.x, snapped.y);
          break;
        case "text":
          get("text:tool-click")?.(snapped.x, snapped.y);
          break;
        case "aoe": {
          const aoe = aoePlacementRef.current;
          if (aoe) {
            aoe.handleClick(raw.x, raw.y, e.shiftKey, engine.gridSize);
          }
          break;
        }
        case "location-link":
          if (linkDrawMode === "polygon") {
            const now = Date.now();
            // Double-click timing guard: skip point addition if within 300ms of last click
            if (now - lastPolygonClickTime.current < 300) {
              lastPolygonClickTime.current = now;
              break;
            }
            lastPolygonClickTime.current = now;

            const pts = linkPolygonPoints.current;
            // Snap-to-first-point: if close enough and >= 3 points, finalize polygon
            if (pts.length >= 3) {
              const cam = engine.cameraRef.current;
              const firstSx = pts[0].x * cam.zoom + cam.offsetX;
              const firstSy = pts[0].y * cam.zoom + cam.offsetY;
              const clickSx = raw.x * cam.zoom + cam.offsetX;
              const clickSy = raw.y * cam.zoom + cam.offsetY;
              const dist = Math.hypot(clickSx - firstSx, clickSy - firstSy);
              if (dist < 15) {
                get("link:drawn")?.({ type: "polygon", points: [...pts] });
                linkPolygonPoints.current = [];
                engine.markDirty("ui");
                break;
              }
            }

            // Normal: add point
            linkPolygonPoints.current = [...pts, { x: raw.x, y: raw.y }];
            engine.markDirty("ui");
          } else {
            // Rect or circle: start drag
            linkDrawStart.current = { x: raw.x, y: raw.y };
            selectionRect.current = {
              x1: raw.x,
              y1: raw.y,
              x2: raw.x,
              y2: raw.y,
            };
          }
          break;
        case "map-object":
          if (mapObjectDrawMode === "polygon") {
            const now = Date.now();
            if (now - lastMapObjPolygonClickTime.current < 300) {
              lastMapObjPolygonClickTime.current = now;
              break;
            }
            lastMapObjPolygonClickTime.current = now;

            const pts = mapObjPolygonPoints.current;
            if (pts.length >= 3) {
              const cam = engine.cameraRef.current;
              const firstSx = pts[0].x * cam.zoom + cam.offsetX;
              const firstSy = pts[0].y * cam.zoom + cam.offsetY;
              const clickSx = raw.x * cam.zoom + cam.offsetX;
              const clickSy = raw.y * cam.zoom + cam.offsetY;
              const dist = Math.hypot(clickSx - firstSx, clickSy - firstSy);
              if (dist < 15) {
                get("map-object:drawn")?.({ type: "polygon", points: [...pts] });
                mapObjPolygonPoints.current = [];
                engine.markDirty("ui");
                break;
              }
            }

            mapObjPolygonPoints.current = [...pts, { x: raw.x, y: raw.y }];
            engine.markDirty("ui");
          } else {
            mapObjDrawStart.current = { x: raw.x, y: raw.y };
            selectionRect.current = {
              x1: raw.x,
              y1: raw.y,
              x2: raw.x,
              y2: raw.y,
            };
          }
          break;
        case "inpaint-rect":
          inpaintRectStart.current = { x: raw.x, y: raw.y };
          inpaintRectEnd.current = { x: raw.x, y: raw.y };
          engine.markDirty("ui");
          break;
        case "inpaint-lasso":
          inpaintLassoPoints.current = [{ x: raw.x, y: raw.y }];
          engine.markDirty("ui");
          break;
        case "detail-rect":
          detailRectStart.current = { x: raw.x, y: raw.y };
          detailRectEnd.current = { x: raw.x, y: raw.y };
          engine.markDirty("ui");
          break;
      }
    },
    [],
  );

  const handleMouseMove = useCallback(
    (e: React.PointerEvent) => {
      const { engine, currentTool, tokens, drawing, fog, snapToGrid, locLinks, isGM, mapObjects: mapObjectsLocal, getTokenDescription, get, linkDrawMode, mapObjectDrawMode } = d.current;
      // Cancel long-press ping on any drag
      if (pingLongPressTimer.current && pingLongPressPos.current) {
        const w0 = engine.mouseToWorld(e.nativeEvent);
        const dx = w0.x - pingLongPressPos.current.x;
        const dy = w0.y - pingLongPressPos.current.y;
        if (dx * dx + dy * dy > 25) { // ~5px world threshold
          clearTimeout(pingLongPressTimer.current);
          pingLongPressTimer.current = 0;
          pingLongPressPos.current = null;
        }
      }

      const w = engine.mouseToWorld(e.nativeEvent);
      const cellX = Math.floor(w.x / engine.gridSize);
      const cellY = Math.floor(w.y / engine.gridSize);
      const coordsEl = document.getElementById("coordinates");
      if (coordsEl) coordsEl.textContent = `${cellX}, ${cellY}`;

      if (isPanning.current) {
        engine.setCamera({
          ...engine.cameraRef.current,
          offsetX: panStart.current.ox + (e.clientX - panStart.current.x),
          offsetY: panStart.current.oy + (e.clientY - panStart.current.y),
        });
        // Fog/drawing use transform-based fast path during pan (no cache rebuild)
        engine.markAllDirty();

        // Debounce fog/drawing cache rebuild until pan settles
        if (zoomCacheTimerRef.current) clearTimeout(zoomCacheTimerRef.current);
        zoomCacheTimerRef.current = setTimeout(() => {
          zoomCacheTimerRef.current = null;
          fog.invalidateCache();
          drawing.invalidateCache();
          engine.markDirty("fog");
          engine.markDirty("drawing");
        }, 150);
        setHoveredNote(null);
        return;
      }

      // Note hover detection (when not dragging)
      if (!isMouseDown.current) {
        const note = getNoteAtWorld(w.x, w.y);
        if (note) {
          const cam = engine.camera;
          const sx = note.x * cam.zoom + cam.offsetX;
          const sy = note.y * cam.zoom + cam.offsetY;
          const rect = engine.refs.current.ui?.getBoundingClientRect();
          setHoveredNote({
            note,
            sx: sx + (rect?.left ?? 0),
            sy: sy + (rect?.top ?? 0) - 30,
          });
        } else {
          setHoveredNote(null);
        }
      }

      // Token hover tooltip (when mouse not pressed, select tool active)
      if (!isMouseDown.current && currentTool === "select") {
        const t = tokens.getTokenAt(w.x, w.y, tokenHitFilter);
        const tid = t?.id ?? null;
        if (tid !== hoverTokenIdRef.current) {
          hoverTokenIdRef.current = tid;
          if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
          const desc = t
            ? getTokenDescription
              ? getTokenDescription(t)
              : t.description
            : "";
          if (t && desc) {
            const sx = e.clientX;
            const sy = e.clientY;
            hoverTimerRef.current = setTimeout(() => {
              setHoverToken({
                token: t,
                description: desc,
                screenX: sx,
                screenY: sy,
              });
            }, 300);
          } else {
            setHoverToken(null);
            hoverTokenIdRef.current = null;
          }
        }
      }

      // MapObject hover detection (C1: between tokens and location links)
      if (
        !isMouseDown.current &&
        (currentTool === "select" || currentTool === "map-object") &&
        mapObjectsLocal
      ) {
        const t = tokens.getTokenAt(w.x, w.y, tokenHitFilter);
        // Only detect mapObject hover when NOT hovering a token
        if (!t) {
          const obj = mapObjectsLocal.getObjectAtWorld(w.x, w.y);
          const prevHovId = mapObjectsLocal.hoveredObjectId.current;
          const newHovId = obj?.id ?? null;
          if (prevHovId !== newHovId) {
            mapObjectsLocal.hoveredObjectId.current = newHovId;
            engine.markDirty("ui");
            get("map-object:hover")?.(obj ?? null, e.clientX, e.clientY);
          }
        } else if (mapObjectsLocal.hoveredObjectId.current) {
          mapObjectsLocal.hoveredObjectId.current = null;
          engine.markDirty("ui");
          get("map-object:hover")?.(null, e.clientX, e.clientY);
        }
      }

      // Location link hover detection (when mouse not pressed, select tool)
      if (!isMouseDown.current && currentTool === "select" && locLinks) {
        const lw = engine.mouseToWorld(e.nativeEvent);
        const link = locLinks.getLinkAtWorld(lw.x, lw.y);
        const visibleLink =
          link && (locLinks.isLinkVisible(link) || isGM) ? link : null;
        const prevHovered = locLinks.hoveredLinkId.current;
        const newHovered = visibleLink?.id ?? null;
        if (prevHovered !== newHovered) {
          locLinks.hoveredLinkId.current = newHovered;
          engine.markDirty("token");
          if (visibleLink?.label) {
            setHoveredLinkTooltip({
              label: visibleLink.label,
              sx: e.clientX,
              sy: e.clientY,
            });
          } else {
            setHoveredLinkTooltip(null);
          }
        } else if (newHovered) {
          setHoveredLinkTooltip((prev) =>
            prev ? { ...prev, sx: e.clientX, sy: e.clientY } : prev,
          );
        }
      } else if (!isMouseDown.current && currentTool !== "select" && locLinks) {
        // Clear hover when switching away from select tool
        if (locLinks.hoveredLinkId.current) {
          locLinks.hoveredLinkId.current = null;
          setHoveredLinkTooltip(null);
          engine.markDirty("token");
        }
      }

      const { raw, snapped } = getWorldPos(e);

      // Track last world position for Q-key waypoint insertion
      lastWorldPos.current = { x: snapped.x, y: snapped.y };

      // Measure tool: always update the live cursor (even without mouse button held)
      if (currentTool === "measure") {
        drawing.updateMeasure(snapped.x, snapped.y);
      }

      // AoE placement: always track cursor for preview
      const aoeRef = aoePlacementRef.current;
      if (currentTool === "aoe" && aoeRef && aoeRef.state !== "idle") {
        aoeRef.handleMouseMove(raw.x, raw.y, e.shiftKey, engine.gridSize);
        engine.markDirty("ui");
      }

      if (!isMouseDown.current) return;

      switch (currentTool) {
        case "select":
          if (dragTokens.current) {
            const wasAlreadyDragging = didDragRef.current;
            didDragRef.current = true;
            for (const dt of dragTokens.current.items) {
              const nx = snapToGrid
                ? engine.snapToGridCenter(
                    raw.x + dt.offsetX,
                    raw.y + dt.offsetY,
                  ).x
                : raw.x + dt.offsetX;
              const ny = snapToGrid
                ? engine.snapToGridCenter(
                    raw.x + dt.offsetX,
                    raw.y + dt.offsetY,
                  ).y
                : raw.y + dt.offsetY;
              // Mutate token position directly in the ref array — no React state update during drag.
              // Look up by ID because tokensRef.current may have been replaced by a state update.
              const refToken = tokens.tokensRef.current.find(
                (t) => t.id === dt.token.id,
              );
              if (refToken) {
                refToken.x = nx;
                refToken.y = ny;
              }
              // Keep drag ref in sync for mouseUp commit
              dt.token.x = nx;
              dt.token.y = ny;
            }
            engine.markDirty("token");
            // Token drag ruler: start on first move, update on subsequent moves
            if (dragTokens.current.items.length > 0) {
              const dt0 = dragTokens.current.items[0];
              if (
                !wasAlreadyDragging &&
                !tokenDragRulerStarted.current &&
                dragTokenOrigin.current
              ) {
                drawing.startTokenDragRuler(
                  dragTokenOrigin.current.x,
                  dragTokenOrigin.current.y,
                );
                tokenDragRulerStarted.current = true;
              }
              drawing.updateTokenDragRuler(dt0.token.x, dt0.token.y);
            }
          } else if (dragMapObject.current && mapObjectsLocal) {
            didDragRef.current = true;
            const dm = dragMapObject.current;
            const dx = raw.x - dm.startX;
            const dy = raw.y - dm.startY;
            // Find the object in the ref array and move its region
            const obj = mapObjectsLocal.objects.current.find(
              (o) => o.id === dm.objectId,
            );
            if (obj) {
              const origRegion = dm.origRegion;
              switch (origRegion.type) {
                case "rect":
                  (obj.region as typeof origRegion).x = origRegion.x + dx;
                  (obj.region as typeof origRegion).y = origRegion.y + dy;
                  break;
                case "circle":
                  (obj.region as typeof origRegion).cx = origRegion.cx + dx;
                  (obj.region as typeof origRegion).cy = origRegion.cy + dy;
                  break;
                case "polygon":
                  (obj.region as typeof origRegion).points =
                    origRegion.points.map((p) => ({
                      x: p.x + dx,
                      y: p.y + dy,
                    }));
                  break;
              }
              engine.markDirty("ui");
            }
          } else if (dragPin.current) {
            didDragRef.current = true;
            const dp = dragPin.current;
            const pinObj = d.current.pins.find(p => p.id === dp.pinId);
            if (pinObj) {
              pinObj.x = raw.x + dp.offsetX;
              pinObj.y = raw.y + dp.offsetY;
              engine.markDirty("token");
            }
          } else if (dragNote.current) {
            didDragRef.current = true;
            const dn = dragNote.current;
            dn.note.x = raw.x + dn.offsetX;
            dn.note.y = raw.y + dn.offsetY;
            engine.markDirty("token");
          } else if (selectionRect.current) {
            selectionRect.current.x2 = raw.x;
            selectionRect.current.y2 = raw.y;
            engine.markDirty("ui");
          }
          break;
        case "select-drawing": {
          if (drawingSelection) {
            const ds = drawingSelection.state.current;
            if (ds.mode === "selecting") {
              drawingSelection.updateMarquee(raw.x, raw.y);
            } else if (ds.mode === "moving") {
              drawingSelection.updateMove(raw.x, raw.y);
            }
          }
          break;
        }
        case "draw":
        case "erase":
        case "shape":
          drawing.continueStroke(snapped.x, snapped.y, currentTool);
          break;
        case "fog":
          if (fog.fogMode.current === "rect") {
            fog.updateRectPreview(snapped.x, snapped.y);
          } else if (fog.fogMode.current !== "polygon") {
            // brush mode
            if (fog.reFogMode.current) {
              fog.continueClose(snapped.x, snapped.y);
            } else {
              fog.continueReveal(snapped.x, snapped.y);
            }
          }
          break;
        case "location-link":
          if (
            linkDrawMode !== "polygon" &&
            linkDrawStart.current &&
            selectionRect.current
          ) {
            selectionRect.current.x2 = raw.x;
            selectionRect.current.y2 = raw.y;
            engine.markDirty("ui");
          }
          break;
        case "map-object":
          if (
            mapObjectDrawMode !== "polygon" &&
            mapObjDrawStart.current &&
            selectionRect.current
          ) {
            selectionRect.current.x2 = raw.x;
            selectionRect.current.y2 = raw.y;
            engine.markDirty("ui");
          }
          break;
        case "wall":
          get("wall:tool-mousemove")?.(snapped.x, snapped.y);
          break;
        case "inpaint-rect":
          if (isMouseDown.current && inpaintRectStart.current) {
            inpaintRectEnd.current = { x: raw.x, y: raw.y };
            engine.markDirty("ui");
          }
          break;
        case "inpaint-lasso":
          if (isMouseDown.current && inpaintLassoPoints.current.length > 0) {
            inpaintLassoPoints.current.push({ x: raw.x, y: raw.y });
            engine.markDirty("ui");
          }
          break;
        case "detail-rect":
          if (isMouseDown.current && detailRectStart.current) {
            detailRectEnd.current = { x: raw.x, y: raw.y };
            engine.markDirty("ui");
          }
          break;
      }
    },
    [],
  );

  const handleMouseUp = useCallback(
    (e?: React.PointerEvent) => {
      const { engine, currentTool, tokens, drawing, fog, snapToGrid, isGM, mapObjects: mapObjectsLocal, selectionCtx, sessionCtx, pageId, drawingSelection, locLinks, get, onContextMenuLink, linkDrawMode, mapObjectDrawMode } = d.current;
      // Release pointer capture
      if (e && (e.target as HTMLElement).hasPointerCapture?.(e.pointerId)) {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      }

      // Cancel long-press ping timer
      if (pingLongPressTimer.current) {
        clearTimeout(pingLongPressTimer.current);
        pingLongPressTimer.current = 0;
        pingLongPressPos.current = null;
      }

      if (dragTokens.current && didDragRef.current) {
        const items = dragTokens.current.items;
        // Commit final positions to React state (was only mutated in ref during drag)
        for (const dt of items) {
          tokens.updateToken(dt.token.id, { x: dt.token.x, y: dt.token.y });
          get("token:move-end")?.(
            dt.token.id,
            dt.token.x,
            dt.token.y,
            dt.origX,
            dt.origY,
          );
        }
        // Batch callback for undo grouping (single and multi-token drag)
        {
          const batchHandler = get("token:batch-move-end");
          if (batchHandler) {
            batchHandler(
              items.map((dt) => ({
                tokenId: dt.token.id,
                x: dt.token.x,
                y: dt.token.y,
                prevX: dt.origX,
                prevY: dt.origY,
                tokenName: dt.token.name,
              })),
            );
          }
        }
      }
      // End token drag ruler
      if (tokenDragRulerStarted.current) {
        drawing.endTokenDragRuler();
        tokenDragRulerStarted.current = false;
      }
      dragTokenOrigin.current = null;

      if (dragPin.current && didDragRef.current) {
        const dp = dragPin.current;
        const pinObj = d.current.pins.find(p => p.id === dp.pinId);
        if (pinObj) {
          const { pins: pinsHook } = tokenCtx;
          pinsHook.movePin(pageId, dp.pinId, pinObj.x, pinObj.y);
        }
      }
      dragPin.current = null;

      if (dragNote.current && didDragRef.current) {
        const dn = dragNote.current;
        get("note:move-end")?.(dn.note.id, dn.note.x, dn.note.y);
      }
      dragNote.current = null;

      // MapObject drag end: send updated region to server
      if (dragMapObject.current && didDragRef.current && mapObjectsLocal) {
        const obj = mapObjectsLocal.objects.current.find(
          (o) => o.id === dragMapObject.current!.objectId,
        );
        if (obj) {
          get("map-object:move-end")?.(obj.id, JSON.parse(JSON.stringify(obj.region)));
        }
      }
      dragMapObject.current = null;

      // Click-to-inspect on notes (no drag) — skip right-click (handled by context menu)
      // Only trigger for select tool to prevent interference with text/drawing tools
      if (
        !didDragRef.current &&
        !dragTokens.current &&
        !clickedTokenRef.current &&
        e?.button !== 2 &&
        currentTool === "select"
      ) {
        if (e) {
          const w = engine.mouseToWorld(e.nativeEvent);
          const n = getNoteAtWorld(w.x, w.y);
          if (n) {
            get("note:click")?.(n);
          } else if (mapObjectsLocal) {
            // Click on MapObject → pin tooltip (for interactable objects)
            const mo = mapObjectsLocal.getObjectAtWorld(w.x, w.y);
            if (mo && mo.interactable) {
              get("map-object:click-pin")?.(mo, e.clientX, e.clientY);
            } else {
              // Dismiss pinned tooltip when clicking elsewhere
              get("map-object:click-pin")?.(null, 0, 0);
              const linkClickHandler = get("link:click");
              if (locLinks && linkClickHandler) {
                const link = locLinks.getLinkAtWorld(w.x, w.y);
                if (link && (locLinks.isLinkVisible(link) || isGM)) {
                  linkClickHandler(link);
                }
              }
            }
          } else {
            const linkClickHandler = get("link:click");
            if (locLinks && linkClickHandler) {
              // Click on location link zone (no note/token under cursor)
              const link = locLinks.getLinkAtWorld(w.x, w.y);
              if (link && (locLinks.isLinkVisible(link) || isGM)) {
                linkClickHandler(link);
              }
            }
          }
        }
      }

      // Reset double-click suppression after timeout
      if (clickedTokenRef.current && !didDragRef.current && !doubleClickSuppressRef.current) {
        setTimeout(() => {
          doubleClickSuppressRef.current = false;
        }, 200);
      }
      // Capture selectionRect before it's cleared (used by link drawing finalization below)
      const savedSelRect = selectionRect.current;
      // Marquee selection: select token(s) inside rect
      if (selectionRect.current) {
        const r = selectionRect.current;
        const minX = Math.min(r.x1, r.x2);
        const maxX = Math.max(r.x1, r.x2);
        const minY = Math.min(r.y1, r.y2);
        const maxY = Math.max(r.y1, r.y2);
        // Only select if dragged a meaningful distance
        if (maxX - minX > 2 || maxY - minY > 2) {
          const matched: string[] = [];
          for (const t of tokens.tokens) {
            if (!t.visible) continue;
            if (t.x >= minX && t.x <= maxX && t.y >= minY && t.y <= maxY) {
              matched.push(t.id);
            }
          }
          tokens.selectTokens(matched);
          // Wire marquee result to SelectionContext
          if (matched.length === 1) {
            selectionCtx.select({ type: 'token', tokenId: matched[0], pageId });
          } else if (matched.length > 1) {
            selectionCtx.select({ type: 'tokens', tokenIds: matched, pageId });
          } else {
            selectionCtx.clearSelection();
          }
        }
        selectionRect.current = null;
        engine.markDirty("ui");
      }

      clickedTokenRef.current = null;
      didDragRef.current = false;

      // Finalize drawing selection operations
      if (currentTool === "select-drawing" && drawingSelection) {
        const ds = drawingSelection.state.current;
        if (ds.mode === "selecting") {
          drawingSelection.endMarquee();
          // Wire marquee result to SelectionContext
          const ids = drawingSelection.getSelectedIds();
          if (ids.length > 0) {
            selectionCtx.select({ type: 'drawing', strokeId: ids[0], pageId });
          } else {
            selectionCtx.clearSelection();
          }
        } else if (ds.mode === "moving") {
          const result = drawingSelection.endMove();
          if (result) {
            get("drawing:move-end")?.(
              result.strokeIds,
              result.dx,
              result.dy,
              result.noteIds,
            );
          }
        }
      }

      isMouseDown.current = false;
      isPanning.current = false;
      dragTokens.current = null;
      // Fire drawing stroke callback before endStroke (stroke is about to be pushed)
      const strokesBefore = drawing.strokes.current.length;
      drawing.endStroke();
      if (drawing.strokes.current.length > strokesBefore) {
        const newStroke =
          drawing.strokes.current[drawing.strokes.current.length - 1];
        get("drawing:stroke-end")?.(newStroke);
      }
      // Measure tool: distinguish click (waypoint mode) from drag (end immediately)
      if (
        e &&
        currentTool === "measure" &&
        measureDownScreenPos.current !== null
      ) {
        const dx = e.clientX - measureDownScreenPos.current.x;
        const dy = e.clientY - measureDownScreenPos.current.y;
        const moved = Math.sqrt(dx * dx + dy * dy);
        if (moved < MEASURE_DRAG_THRESHOLD) {
          // It was a click, not a drag → waypoint mode
          if (!measureActive.current) {
            measureActive.current = true; // first click: activate waypoint mode
          } else {
            // subsequent click: add waypoint
            const { snapped } = getWorldPos(e);
            drawing.addWaypoint(snapped.x, snapped.y);
          }
        } else if (!measureActive.current) {
          // It was a drag → end measurement immediately
          // (but if Q was pressed during drag, we switched to waypoint mode — keep going)
          drawing.endMeasure();
        }
        measureDownScreenPos.current = null;
      }
      // Finalize link drawing (rect/circle mode)
      if (
        currentTool === "location-link" &&
        linkDrawStart.current &&
        linkDrawMode !== "polygon"
      ) {
        const s = linkDrawStart.current;
        const end = savedSelRect;
        const linkDrawnFinalizer = get("link:drawn");
        if (end && linkDrawnFinalizer) {
          if (linkDrawMode === "circle") {
            const dx = end.x2 - s.x;
            const dy = end.y2 - s.y;
            const radius = Math.sqrt(dx * dx + dy * dy);
            if (radius > 5) {
              linkDrawnFinalizer({ type: "circle", cx: s.x, cy: s.y, radius });
            }
          } else {
            // rect
            const x = Math.min(s.x, end.x2);
            const y = Math.min(s.y, end.y2);
            const width = Math.abs(end.x2 - s.x);
            const height = Math.abs(end.y2 - s.y);
            if (width > 5 && height > 5) {
              linkDrawnFinalizer({ type: "rect", x, y, width, height });
            }
          }
        }
        linkDrawStart.current = null;
        selectionRect.current = null;
        engine.markDirty("ui");
      }
      // Finalize map-object drawing (rect/circle mode)
      if (
        currentTool === "map-object" &&
        mapObjDrawStart.current &&
        mapObjectDrawMode !== "polygon"
      ) {
        const s = mapObjDrawStart.current;
        const end = savedSelRect;
        const mapObjDrawnFinalizer = get("map-object:drawn");
        if (end && mapObjDrawnFinalizer) {
          if (mapObjectDrawMode === "circle") {
            const dx = end.x2 - s.x;
            const dy = end.y2 - s.y;
            const radius = Math.sqrt(dx * dx + dy * dy);
            if (radius > 5) {
              mapObjDrawnFinalizer({ type: "circle", cx: s.x, cy: s.y, radius });
            }
          } else {
            const x = Math.min(s.x, end.x2);
            const y = Math.min(s.y, end.y2);
            const width = Math.abs(end.x2 - s.x);
            const height = Math.abs(end.y2 - s.y);
            if (width > 5 && height > 5) {
              mapObjDrawnFinalizer({ type: "rect", x, y, width, height });
            }
          }
        }
        mapObjDrawStart.current = null;
        selectionRect.current = null;
        engine.markDirty("ui");
      }

      // Finalize inpaint selection
      if (currentTool === "inpaint-rect" && inpaintRectStart.current && inpaintRectEnd.current) {
        const start = inpaintRectStart.current;
        const end = inpaintRectEnd.current;
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const w = Math.abs(end.x - start.x);
        const h = Math.abs(end.y - start.y);
        if (w > 5 && h > 5) {
          const cam = engine.cameraRef.current;
          inpaintPanelAnchor.current = {
            x: Math.max(x, Math.min(x + w, end.x)) * cam.zoom + cam.offsetX,
            y: Math.max(y, Math.min(y + h, end.y)) * cam.zoom + cam.offsetY + 10,
          };
          d.current.get("inpaint:region-drawn")?.({ type: "rect", x, y, width: w, height: h });
        }
        inpaintRectStart.current = null;
        inpaintRectEnd.current = null;
        engine.markDirty("ui");
      }
      if (currentTool === "inpaint-lasso" && inpaintLassoPoints.current.length >= 3) {
        const points = [...inpaintLassoPoints.current];
        const cam = engine.cameraRef.current;
        // Anchor near the last point
        const lastPt = points[points.length - 1];
        inpaintPanelAnchor.current = {
          x: lastPt.x * cam.zoom + cam.offsetX,
          y: lastPt.y * cam.zoom + cam.offsetY + 10,
        };
        d.current.get("inpaint:region-drawn")?.({ type: "lasso", points });
        inpaintLassoPoints.current = [];
        engine.markDirty("ui");
      } else if (currentTool === "inpaint-lasso") {
        inpaintLassoPoints.current = [];
        engine.markDirty("ui");
      }

      // Finalize detail-rect selection
      if (currentTool === "detail-rect" && detailRectStart.current && detailRectEnd.current) {
        const start = detailRectStart.current;
        const end = detailRectEnd.current;
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const w = Math.abs(end.x - start.x);
        const h = Math.abs(end.y - start.y);
        if (w >= 64 && h >= 64) {
          d.current.get("detail:region-drawn")?.({ x, y, width: w, height: h });
        }
        detailRectStart.current = null;
        detailRectEnd.current = null;
        engine.markDirty("ui");
      }

      // Fire fog reveal/close callbacks for all new reveals/closings (only when using fog tool)
      if (currentTool === "fog") {
        if (fog.fogMode.current === "rect") {
          // Rect mode: finalize on pointer up
          if (e) {
            const { snapped } = getWorldPos(e);
            const prevRevealed = fog.revealed.current.length;
            const prevClosings = fog.closings.current.length;
            fog.endRectReveal(snapped.x, snapped.y);
            // Send newly added rect via WS
            if (fog.reFogMode.current) {
              const newClosings = fog.closings.current.slice(prevClosings);
              if (newClosings.length > 0) get("fog:close-batch")?.(newClosings);
            } else {
              const newReveals = fog.revealed.current.slice(prevRevealed);
              if (newReveals.length > 0) get("fog:reveal-batch")?.(newReveals);
            }
          }
        } else if (fog.fogMode.current !== "polygon") {
          // brush mode
          if (fog.reFogMode.current) {
            const newClosings = fog.closings.current.slice(
              fogCloseStartIdx.current,
            );
            fog.endClose();
            if (newClosings.length > 0) {
              get("fog:close-batch")?.(newClosings);
            }
            fogCloseStartIdx.current = fog.closings.current.length;
          } else {
            const newReveals = fog.revealed.current.slice(
              fogRevealStartIdx.current,
            );
            fog.endReveal();
            if (newReveals.length > 0) {
              get("fog:reveal-batch")?.(newReveals);
            }
            fogRevealStartIdx.current = fog.revealed.current.length;
          }
        }
        // polygon mode: no action on pointer up (points are added on click, finalized on double-click)
      }
      const ui = engine.refs.current.ui;
      // Restore tool-appropriate cursor (not blank) so crosshair etc. persist between clicks
      if (ui && !spaceDown.current && !isPanning.current) {
        const CROSSHAIR_TOOLS = new Set([
          "draw",
          "erase",
          "shape",
          "measure",
          "fog",
          "note",
          "location-link",
          "map-object",
          "text",
          "inpaint-rect",
          "inpaint-lasso",
          "detail-rect",
        ]);
        ui.style.cursor = CROSSHAIR_TOOLS.has(currentTool) ? "crosshair" : "";
      }
      // Clear hover tooltips on interaction
      setHoverToken(null);
      setHoveredLinkTooltip(null);
      if (locLinks) locLinks.hoveredLinkId.current = null;
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      hoverTokenIdRef.current = null;
    },
    [],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const { engine, currentTool, tokens, drawing, locLinks, onContextMenuLink, drawingSelection, get } = d.current;
      e.preventDefault();
      // AoE placement: right-click cancels
      const aoeCtx = aoePlacementRef.current;
      if (currentTool === "aoe" && aoeCtx && aoeCtx.state !== "idle") {
        aoeCtx.handleRightClick();
        engine.markDirty("ui");
        return;
      }
      // Right-click dismisses persistent public rulers
      drawing.clearPublicRulers();
      // Cancel any active measurement
      if (measureActive.current) {
        drawing.endMeasure();
        measureActive.current = false;
        measureDownScreenPos.current = null;
        return;
      }
      const w = engine.mouseToWorld(e.nativeEvent);
      // Check location links first (GM only)
      if (onContextMenuLink && locLinks) {
        const link = locLinks.getLinkAtWorld(w.x, w.y);
        if (link) {
          onContextMenuLink(link, e.clientX, e.clientY);
          return;
        }
      }
      const t = tokens.getTokenAt(w.x, w.y, tokenHitFilter);
      if (t) {
        tokens.selectToken(t.id);
        get("token:context-menu")?.(t, e.clientX, e.clientY);
      } else {
        get("canvas:context-menu")?.(e.clientX, e.clientY, w.x, w.y);
      }
    },
    [],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      const { engine, currentTool, tokens, drawing, fog, isGM, mapObjects: mapObjectsLocal, linkDrawMode, mapObjectDrawMode, get } = d.current;
      // In measure mode, double-click finishes/cancels the measurement
      if (currentTool === "measure") {
        drawing.endMeasure();
        measureActive.current = false;
        measureDownScreenPos.current = null;
        return;
      }
      // Wall tool: double-click finishes chain
      if (currentTool === "wall") {
        get("wall:finish-chain")?.();
        return;
      }
      // Fog polygon mode: double-click finishes polygon
      if (currentTool === "fog" && fog.fogMode.current === "polygon") {
        const prevRevealed = fog.revealed.current.length;
        const prevClosings = fog.closings.current.length;
        fog.finishPolygon();
        if (fog.reFogMode.current) {
          const newClosings = fog.closings.current.slice(prevClosings);
          if (newClosings.length > 0) get("fog:close-batch")?.(newClosings);
        } else {
          const newReveals = fog.revealed.current.slice(prevRevealed);
          if (newReveals.length > 0) get("fog:reveal-batch")?.(newReveals);
        }
        return;
      }
      // Polygon mode: double-click finishes polygon
      if (currentTool === "location-link" && linkDrawMode === "polygon") {
        const pts = linkPolygonPoints.current;
        if (pts.length >= 3) {
          get("link:drawn")?.({ type: "polygon", points: [...pts] });
        }
        linkPolygonPoints.current = [];
        engine.markDirty("ui");
        return;
      }
      // Map-object polygon mode: double-click finishes polygon
      if (currentTool === "map-object" && mapObjectDrawMode === "polygon") {
        const pts = mapObjPolygonPoints.current;
        if (pts.length >= 3) {
          get("map-object:drawn")?.({ type: "polygon", points: [...pts] });
        }
        mapObjPolygonPoints.current = [];
        engine.markDirty("ui");
        return;
      }
      // Text tool: double-click opens edit
      {
        const textDblClickHandler = get("text:double-click");
        if (currentTool === "text" && textDblClickHandler) {
          const w = engine.mouseToWorld(e.nativeEvent);
          textDblClickHandler(w.x, w.y);
          return;
        }
      }
      if (currentTool !== "select") return;
      // Suppress pending click-to-inspect
      doubleClickSuppressRef.current = true;
      const w = engine.mouseToWorld(e.nativeEvent);

      // Check note click first
      const note = getNoteAtWorld(w.x, w.y);
      {
        const noteClickHandler = get("note:click");
        if (note && noteClickHandler) {
          noteClickHandler(note);
          return;
        }
      }

      // Check MapObject double-click (GM only — opens edit dialog)
      {
        const mapObjDblClickHandler = get("map-object:double-click");
        if (isGM && mapObjectsLocal && mapObjDblClickHandler) {
          const mo = mapObjectsLocal.getObjectAtWorld(w.x, w.y);
          if (mo) {
            mapObjDblClickHandler(mo);
            return;
          }
        }
      }

      const t = tokens.getTokenAt(w.x, w.y, tokenHitFilter);
      if (t) {
        get("token:double-click")?.(t);
      } else {
        // Double-click on pin → open editor
        const pin = getPinAtWorld(w.x, w.y);
        if (pin) {
          get("pin:edit")?.(pin);
          return;
        }
        const snap = engine.snapToGridCenter(w.x, w.y);
        get("token:double-click-empty")?.(snap.x, snap.y);
      }
    },
    [],
  );

  // Cursor based on tool
  const getCursor = () => {
    switch (currentTool) {
      case "select":
        if (mapObjectsLocal?.hoveredObjectId.current) {
          const hovObj = mapObjectsLocal.objects.current.find(
            (o) => o.id === mapObjectsLocal.hoveredObjectId.current,
          );
          if (hovObj?.interactable) return "pointer";
        }
        return locLinks?.hoveredLinkId.current ? "pointer" : "default";
      case "select-drawing": {
        if (drawingSelection) {
          const ds = drawingSelection.state.current;
          if (ds.mode === "moving") return "grabbing";
          if (ds.selectedStrokeIds.size > 0 && ds.selectionBounds)
            return "grab";
        }
        return "crosshair";
      }
      case "draw":
      case "erase":
      case "shape":
      case "measure":
      case "fog":
        return "crosshair";
      case "note":
      case "location-link":
      case "map-object":
      case "text":
      case "aoe":
      case "wall":
      case "inpaint-rect":
      case "inpaint-lasso":
      case "detail-rect":
        return "crosshair";
      default:
        return "default";
    }
  };

  const handleMouseLeave = useCallback(() => {
    // Reset click tracking to prevent unintended click events on leave
    clickedTokenRef.current = null;
    didDragRef.current = false;
    selectionRect.current = null;
    handleMouseUp();
    setHoverToken(null);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTokenIdRef.current = null;
  }, []); // handleMouseUp is stable

  // ─── Drag & Drop from SRD / Character panels ───

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const jsonStr = e.dataTransfer.getData("application/json");
      if (!jsonStr) return;

      let payload: { dragType: string; [key: string]: unknown };
      try {
        payload = JSON.parse(jsonStr);
      } catch {
        return;
      }

      // Convert screen (client) coords to world coords using engine helper
      const world = engine.mouseToWorld(e.nativeEvent);

      if (payload.dragType === "srd-monster") {
        const dropMonsterHandler = get("drop:monster");
        if (dropMonsterHandler && typeof payload.slug === "string") {
          dropMonsterHandler(payload.slug, world.x, world.y);
        }
      } else if (payload.dragType === "journal-entry") {
        if (typeof payload.entryId !== "string" || typeof payload.entryName !== "string") return;
        const dropJournalHandler = get("drop:journal-entry");
        if (dropJournalHandler) {
          dropJournalHandler(payload.entryId, payload.entryName, world.x, world.y);
        }
      } else if (payload.dragType === "character-sheet") {
        if (
          typeof payload.sheetId !== "string" ||
          typeof payload.sheetName !== "string"
        )
          return;
        const tokenAtDrop = tokens.getTokenAt(world.x, world.y);
        const dropSheetOnTokenHandler = get("drop:sheet-on-token");
        const dropSheetOnCanvasHandler = get("drop:sheet-on-canvas");
        if (tokenAtDrop && dropSheetOnTokenHandler) {
          // Drop onto existing token → bind sheet
          dropSheetOnTokenHandler(payload.sheetId, payload.sheetName, tokenAtDrop);
        } else if (!tokenAtDrop && dropSheetOnCanvasHandler) {
          // Drop onto empty canvas → create token with sheet
          dropSheetOnCanvasHandler(
            payload.sheetId,
            payload.sheetName,
            world.x,
            world.y,
          );
        }
      }
    },
    [engine, tokens],
  );

  return (
    <div
      id="canvas-container"
      data-component="CanvasArea"
      ref={engine.containerRef}
    >
      <canvas
        data-testid="canvas-map"
        ref={(el) => {
          engine.refs.current.map = el;
        }}
      />
      <canvas
        data-testid="canvas-grid"
        ref={(el) => {
          engine.refs.current.grid = el;
        }}
        style={{ pointerEvents: "none" }}
      />
      <canvas
        data-testid="canvas-token"
        ref={(el) => {
          engine.refs.current.token = el;
        }}
        style={{ pointerEvents: "none" }}
      />
      <canvas
        data-testid="canvas-drawing"
        ref={(el) => {
          engine.refs.current.drawing = el;
        }}
        style={{ pointerEvents: "none" }}
      />
      <canvas
        data-testid="canvas-fog"
        ref={(el) => {
          engine.refs.current.fog = el;
        }}
        style={{ pointerEvents: "none" }}
      />
      <canvas
        data-testid="canvas-ui"
        ref={(el) => {
          engine.refs.current.ui = el;
        }}
        onPointerDown={handleMouseDown}
        onPointerMove={handleMouseMove}
        onPointerUp={handleMouseUp}
        onPointerLeave={handleMouseLeave}
        style={{ cursor: getCursor(), zIndex: 'var(--z-canvas-ui)', touchAction: "none" }}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
      {/* Ambient time-of-day overlay */}
      {sessionCtx.sessionSettings?.ambientOverlayEnabled &&
        sessionCtx.mp.serverState?.timelineCursor?.timeOfDay && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: TIME_OF_DAY_OVERLAY[sessionCtx.mp.serverState.timelineCursor.timeOfDay],
            zIndex: 'var(--z-canvas-ui-above)',
            transition: 'background-color 1.5s ease',
          }}
        />
      )}
      {/* Time-of-day HUD */}
      {sessionCtx.mp.serverState?.timelineCursor?.timeOfDay && (
        <TimeOfDayHud
          cursor={sessionCtx.mp.serverState.timelineCursor}
          calendar={sessionCtx.mp.serverState.calendar}
        />
      )}
      {/* Character HUD overlay */}
      <CharacterHudOverlay
        directSheetId={directSheetId}
        onClearDirectSheetId={onClearDirectSheetId}
      />
      <CharacterPortraits
        onOpenSheet={onOpenSheet}
        avatarSize={avatarSize}
        onAvatarSizeChange={onAvatarSizeChange}
      />
      <div id="zoom-controls">
        <button onClick={() => engine.setZoom(engine.camera.zoom * ZOOM_STEP)}>
          +
        </button>
        <span id="zoom-level">{Math.round(engine.camera.zoom * 100)}%</span>
        <button onClick={() => engine.setZoom(engine.camera.zoom / ZOOM_STEP)}>
          −
        </button>
        <button onClick={() => engine.fitToView()}>⊡</button>
      </div>
      <div id="coordinates">0, 0</div>
      {inpainting && inpainting.region && (currentTool === "inpaint-rect" || currentTool === "inpaint-lasso") && (
        <InpaintPromptPanel
          inpainting={inpainting}
          anchorX={inpaintPanelAnchor.current.x}
          anchorY={inpaintPanelAnchor.current.y}
        />
      )}
      {previewLabel && (
        <div className="canvas-preview-indicator">
          {t("pages.preview")}: {previewLabel}
        </div>
      )}
      {resolvedTokenActions &&
        resolvedTokenActions.length > 0 &&
        resolvedOnTokenAction &&
        (() => {
          // Compute selected token's screen position for HUD placement
          const sel = tokens.selectedToken;
          const cam = engine.cameraRef.current;
          const gs = engine.gridSize;
          const screenX = sel ? sel.x * cam.zoom + cam.offsetX : 0;
          const screenY = sel ? sel.y * cam.zoom + cam.offsetY : 0;
          const tokenPxSize = sel ? sel.size * gs * cam.zoom : 0;
          return (
            <TokenActionBar
              actions={resolvedTokenActions}
              onAction={resolvedOnTokenAction}
              tokenScreenPos={{ x: screenX, y: screenY }}
              tokenSize={tokenPxSize}
            />
          );
        })()}
      {hoveredNote && (
        <div
          className="fixed z-[var(--z-canvas-ui)] px-2 py-1 text-xs rounded bg-zinc-800 text-white border border-zinc-600 pointer-events-none"
          style={{
            left: hoveredNote.sx,
            top: hoveredNote.sy,
            transform: "translate(-50%, -100%)",
          }}
        >
          {hoveredNote.note.title}
          {hoveredNote.note.visibility === "gm" && (
            <span className="ml-1 text-zinc-400">(GM)</span>
          )}
        </div>
      )}
      {hoverToken && (
        <TokenHoverTooltip
          name={hoverToken.token.name}
          description={hoverToken.description}
          screenX={hoverToken.screenX}
          screenY={hoverToken.screenY}
        />
      )}
      {hoveredLinkTooltip && (
        <div
          className="token-hover-tooltip"
          style={{
            position: "fixed",
            left: hoveredLinkTooltip.sx + 12,
            top: hoveredLinkTooltip.sy + 12,
            zIndex: 'var(--z-toast)',
            pointerEvents: "none",
          }}
        >
          <div className="font-semibold text-sm text-zinc-100">
            {hoveredLinkTooltip.label}
          </div>
        </div>
      )}
      {/* Edge expand buttons (Spec 055 Phase 4) */}
      {onOpenOutpaint && (
        <EdgeExpandButtons
          visible={isGM && !!engine.mapImage && currentTool === 'select'}
          onExpand={onOpenOutpaint}
        />
      )}
    </div>
  );
});

export default CanvasArea;
